import React, {useReducer, useEffect, useCallback, useRef, useState} from 'react';
import {useLocation, useNavigate, useSearchParams} from 'react-router-dom';
import styles from '../designs/MathPage.module.css';
import placeholderLogo from '../designs/placeholder.png';
import {useUser} from './UserContext';
import _ from 'lodash';
import {isDemoModeEnabled, isDemoBetUsedLocal, markDemoBetUsedLocal} from '../utils/demoAccess';


// Визначаємо початковий стан
const initialState = {
    apiMessage: '',
    betMessage: '',
    prematchBetMessage: '',
    liveBetMessage: '',
    extendedAnalysisMessage: '',
    activeBetType: null,
    backendMessage: '',
    isLoading: false,
    predictions: null,
    prematchOdds: null,
    liveOdds: null,
    teamStats: null,
    recentMatches: null,
    matchStats: null,
    showStats: true,
    showPredictions: false,
    showTeamStats: false,
    showRecentMatches: false,
    showPrematchOdds: false,
    showLiveOdds: false,
    selectedMarket: 'all',
    liveQuota: null,
    liveQuotaMsg: '',
    lastGeneratedAt: null,   // мітка часу останньої показаної ставки
    prematchGeneratedAt: null,
    liveGeneratedAt: null,
    suppressUntil: null,     // до якого часу НЕ оновлювати з беку (мс)

};

const actions = {
    LOAD_LIVE_BET: 'LOAD_LIVE_BET',
    LOAD_BET: 'LOAD_BET',
    LOAD_OPINION: 'LOAD_OPINION',
    LOAD_PREDICTIONS: 'LOAD_PREDICTIONS',
    LOAD_PREMATCH_ODDS: 'LOAD_PREMATCH_ODDS',
    LOAD_LIVE_ODDS: 'LOAD_LIVE_ODDS',
    LOAD_TEAM_STATS: 'LOAD_TEAM_STATS',
    LOAD_RECENT_MATCHES: 'LOAD_RECENT_MATCHES',
    LOAD_MATCH_STATS: 'LOAD_MATCH_STATS',
    CACHE_LIVE_BET: 'CACHE_LIVE_BET',
    SHOW_BET_TYPE: 'SHOW_BET_TYPE',
    SHOW_STATS: 'SHOW_STATS',
    SET_ERROR: 'SET_ERROR',
    SET_SELECTED_MARKET: 'SET_SELECTED_MARKET',
    SET_TEAM_STATS_CACHE: 'SET_TEAM_STATS_CACHE'
};

const DEFAULT_ANALYSIS_AVAILABILITY = {
    loading: true,
    checked: false,
    has_recent_info: null,
    has_prematch_odds: null,
    can_get_bet: true,
    can_get_extended: true,
    reason: '',
    simple_warning: '',
};

const GENERATION_SOUND_SOURCES = (process.env.REACT_APP_GENERATION_SOUND_URLS || '')
    .split(',')
    .map((src) => src.trim())
    .filter(Boolean);
const DEFAULT_GENERATION_SOUND_SOURCES = [
    '/sounds/generation1.mp3',
    '/sounds/generation2.mp3',
    '/sounds/generation3.mp3',
];
const GENERATION_READY_SOUND_SRC = process.env.REACT_APP_GENERATION_READY_SOUND_URL || '/sounds/ready.mp3';
const GENERATION_SOUND_ENABLED_KEY = 'grachGenerationSoundEnabled';
const GENERATION_SOUND_VOLUME_KEY = 'grachGenerationSoundVolume';
const DEFAULT_GENERATION_SOUND_VOLUME = Number(process.env.REACT_APP_GENERATION_SOUND_VOLUME || '0.35');
const MIN_GENERATION_SOUND_VOLUME = 0.1;
const USER_MATCH_BET_VIEW_PREFIX = 'grachSeenBetForMatch';
const FIRST_MATCH_BET_LOADING_MS = 3200;
const CACHED_MATCH_BET_FAKE_LOADING_MS = 5600;
const FAST_GENERATION_RESPONSE_MS = 4500;

const delay = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

const getGenerationProgressTarget = (elapsedMs) => {
    if (elapsedMs <= 1200) {
        return 6 + (elapsedMs / 1200) * 28;
    }
    if (elapsedMs <= 5200) {
        const progress = (elapsedMs - 1200) / 4000;
        return 34 + (1 - Math.pow(1 - progress, 1.8)) * 43;
    }
    if (elapsedMs <= 14000) {
        const progress = (elapsedMs - 5200) / 8800;
        return 77 + (1 - Math.pow(1 - progress, 1.4)) * 15;
    }
    return Math.min(96, 92 + (1 - Math.exp(-(elapsedMs - 14000) / 7000)) * 4);
};

const getGenerationSoundVolume = () => {
    if (typeof window === 'undefined') return DEFAULT_GENERATION_SOUND_VOLUME;
    const raw = window.localStorage.getItem(GENERATION_SOUND_VOLUME_KEY);
    if (raw === null || raw === '') return DEFAULT_GENERATION_SOUND_VOLUME;
    const saved = Number(raw);
    const volume = Number.isFinite(saved) && saved > 0 ? saved : DEFAULT_GENERATION_SOUND_VOLUME;
    return Math.max(MIN_GENERATION_SOUND_VOLUME, Math.min(1, volume));
};

const isGenerationSoundEnabled = () => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(GENERATION_SOUND_ENABLED_KEY) !== 'false';
};

const getGenerationSoundSources = () => (
    GENERATION_SOUND_SOURCES.length ? GENERATION_SOUND_SOURCES : DEFAULT_GENERATION_SOUND_SOURCES
);

const normalizeBetViewKeyPart = (value, fallback = '') => (
    String(value || fallback)
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
);

const getUserMatchBetViewKeys = ({telegramId, type, matchName, date, fixtureId}) => {
    const normalizedType = normalizeBetViewKeyPart(type, 'prematch');
    const normalizedMatch = normalizeBetViewKeyPart(matchName, 'match');
    const normalizedDate = normalizeBetViewKeyPart(date, 'date');
    const normalizedFixture = normalizeBetViewKeyPart(fixtureId, '');
    const normalizedUser = normalizeBetViewKeyPart(telegramId, '');
    const matchIdentityParts = [
        normalizedFixture ? `fixture:${normalizedFixture}` : '',
        normalizedMatch ? `match:${normalizedMatch}` : '',
    ].filter(Boolean);
    const userParts = [
        normalizedUser ? `user:${normalizedUser}` : '',
        'user:any',
    ].filter(Boolean);

    const keys = [];
    userParts.forEach((userPart) => {
        matchIdentityParts.forEach((matchPart) => {
            keys.push(`${USER_MATCH_BET_VIEW_PREFIX}|${userPart}|${normalizedType}|${matchPart}|${normalizedDate}`);
        });
    });
    keys.push([
        USER_MATCH_BET_VIEW_PREFIX,
        telegramId || 'guest',
        type || 'prematch',
        fixtureId || matchName || 'match',
        date || 'date',
    ].join('|'));
    return [...new Set(keys)].map((key) => encodeURIComponent(key));
};

const hasUserSeenMatchBet = (params) => {
    if (typeof window === 'undefined') return false;
    return getUserMatchBetViewKeys(params).some((key) => (
        window.localStorage.getItem(key) === 'true'
        || window.sessionStorage.getItem(key) === 'true'
    ));
};

const markUserSeenMatchBet = (params) => {
    if (typeof window === 'undefined') return;
    getUserMatchBetViewKeys(params).forEach((key) => {
        window.localStorage.setItem(key, 'true');
        window.sessionStorage.setItem(key, 'true');
    });
};

const createGenerationToneUrl = () => {
    const sampleRate = 8000;
    const duration = 0.28;
    const samples = Math.floor(sampleRate * duration);
    const dataSize = samples * 2;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeText = (offset, text) => {
        for (let i = 0; i < text.length; i += 1) {
            view.setUint8(offset + i, text.charCodeAt(i));
        }
    };

    writeText(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeText(8, 'WAVE');
    writeText(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeText(36, 'data');
    view.setUint32(40, dataSize, true);

    for (let i = 0; i < samples; i += 1) {
        const t = i / sampleRate;
        const fadeIn = Math.min(1, i / 180);
        const fadeOut = Math.min(1, (samples - i) / 260);
        const envelope = Math.min(fadeIn, fadeOut) * 0.22;
        const wave = Math.sin(2 * Math.PI * 440 * t) * 0.75
            + Math.sin(2 * Math.PI * 660 * t) * 0.25;
        view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, wave * envelope)) * 32767, true);
    }

    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return `data:audio/wav;base64,${window.btoa(binary)}`;
};

const createGenerationReadyToneUrl = () => {
    const sampleRate = 12000;
    const duration = 0.38;
    const samples = Math.floor(sampleRate * duration);
    const dataSize = samples * 2;
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeText = (offset, text) => {
        for (let i = 0; i < text.length; i += 1) {
            view.setUint8(offset + i, text.charCodeAt(i));
        }
    };

    writeText(0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeText(8, 'WAVE');
    writeText(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeText(36, 'data');
    view.setUint32(40, dataSize, true);

    for (let i = 0; i < samples; i += 1) {
        const t = i / sampleRate;
        const fadeIn = Math.min(1, i / 120);
        const fadeOut = Math.min(1, (samples - i) / 1500);
        const envelope = Math.min(fadeIn, fadeOut) * 0.32;
        const base = t < 0.16 ? 740 : 980;
        const wave = Math.sin(2 * Math.PI * base * t) * 0.72
            + Math.sin(2 * Math.PI * (base * 1.5) * t) * 0.18;
        view.setInt16(44 + i * 2, Math.max(-1, Math.min(1, wave * envelope)) * 32767, true);
    }

    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i += 1) {
        binary += String.fromCharCode(bytes[i]);
    }
    return `data:audio/wav;base64,${window.btoa(binary)}`;
};

const createGenerationSoundElement = () => {
    const fallbackSrc = createGenerationToneUrl();
    const sources = getGenerationSoundSources();
    const randomSrc = sources[Math.floor(Math.random() * sources.length)] || fallbackSrc;
    const element = new Audio(randomSrc);
    element.loop = true;
    element.volume = getGenerationSoundVolume();
    element.preload = 'auto';
    element.addEventListener('error', () => {
        if (element.src === fallbackSrc) return;
        element.src = fallbackSrc;
        element.play?.().catch?.((error) => {
            console.debug('Generation fallback sound unavailable:', error?.message || error);
        });
    }, {once: true});
    return element;
};

const fadeOutGenerationSoundElement = (element, keepSource = false) => {
    if (!element) return;
    const startVolume = element.volume ?? getGenerationSoundVolume();
    const fadeMs = 700;
    const startedAt = performance.now();

    const step = (now) => {
        const progress = Math.min(1, (now - startedAt) / fadeMs);
        element.volume = Math.max(0, startVolume * (1 - progress));
        if (progress < 1) {
            window.requestAnimationFrame(step);
            return;
        }
        try {
            element.pause();
            element.currentTime = 0;
            element.volume = getGenerationSoundVolume();
            if (!keepSource) {
                element.removeAttribute('src');
                element.load?.();
            }
        } catch (_) {
        }
    };

    window.requestAnimationFrame(step);
};

const createGenerationReadySoundElement = () => {
    const element = new Audio(GENERATION_READY_SOUND_SRC);
    element.volume = getGenerationSoundVolume();
    element.preload = 'auto';
    element.addEventListener('error', () => {
        console.debug('Generation ready sound file unavailable:', GENERATION_READY_SOUND_SRC);
    }, {once: true});
    return element;
};

const playGenerationReadySound = (preloadedElement = null) => {
    if (!isGenerationSoundEnabled()) return;
    const volume = getGenerationSoundVolume();
    if (volume <= 0) return;
    try {
        const element = preloadedElement || createGenerationReadySoundElement();
        element.pause();
        element.currentTime = 0;
        element.volume = volume;
        element.play?.().catch?.((error) => {
            console.debug('Generation ready sound unavailable:', error?.message || error);
        });
    } catch (error) {
        console.debug('Generation ready sound init failed:', error?.message || error);
    }
};

// Reducer для управління станами
const reducer = (state, action) => {
    switch (action.type) {
        case actions.LOAD_LIVE_BET:
            {
                const hasLiveMessage = !action.payload.isLoading && action.payload.message !== undefined;
                const liveMessage = hasLiveMessage ? action.payload.message : state.liveBetMessage;
                return {
                    ...state,
                    isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                    apiMessage: action.payload.isLoading ? '' : (hasLiveMessage ? liveMessage : state.apiMessage),
                    betMessage: action.payload.isLoading ? state.betMessage : (hasLiveMessage ? liveMessage : state.betMessage),
                    liveBetMessage: liveMessage || '',
                    activeBetType: action.payload.isLoading || hasLiveMessage ? 'live' : state.activeBetType,
                    backendMessage: '',
                    showStats: false,
                    showPredictions: false,
                    showTeamStats: false,
                    showRecentMatches: false,
                    showPrematchOdds: false,
                    showLiveOdds: false,
                    matchStats: action.payload.matchStats || state.matchStats,
                    lastGeneratedAt: action.payload.generated_time || state.lastGeneratedAt,
                    liveGeneratedAt: action.payload.generated_time || state.liveGeneratedAt,
                };
            }
        case actions.LOAD_BET:
            {
                const hasPrematchMessage = !action.payload.isLoading && action.payload.message !== undefined;
                const prematchMessage = hasPrematchMessage ? action.payload.message : state.prematchBetMessage;
                return {
                    ...state,
                    isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                    apiMessage: action.payload.isLoading ? '' : (hasPrematchMessage ? prematchMessage : state.apiMessage),
                    betMessage: action.payload.isLoading ? state.betMessage : (hasPrematchMessage ? prematchMessage : state.betMessage),
                    prematchBetMessage: prematchMessage || '',
                    activeBetType: action.payload.isLoading || hasPrematchMessage ? 'prematch' : state.activeBetType,
                    backendMessage: '',
                    showStats: false,
                    showPredictions: false,
                    showTeamStats: false,
                    showRecentMatches: false,
                    showPrematchOdds: false,
                    showLiveOdds: false,
                    lastGeneratedAt: action.payload.generated_time || state.lastGeneratedAt,
                    prematchGeneratedAt: action.payload.generated_time || state.prematchGeneratedAt,
                };
            }
        case actions.CACHE_LIVE_BET:
            return {
                ...state,
                liveBetMessage: action.payload.message || state.liveBetMessage,
                matchStats: action.payload.matchStats || state.matchStats,
                lastGeneratedAt: action.payload.generated_time || state.lastGeneratedAt,
                liveGeneratedAt: action.payload.generated_time || state.liveGeneratedAt,
            };
        case actions.SHOW_BET_TYPE:
            if (action.payload.betType === 'live' && state.liveBetMessage) {
                return {
                    ...state,
                    apiMessage: state.liveBetMessage,
                    betMessage: state.liveBetMessage,
                    activeBetType: 'live',
                    backendMessage: '',
                    showStats: false,
                    showPredictions: false,
                    showTeamStats: false,
                    showRecentMatches: false,
                    showPrematchOdds: false,
                    showLiveOdds: false,
                };
            }
            if (action.payload.betType === 'prematch' && state.prematchBetMessage) {
                return {
                    ...state,
                    apiMessage: state.prematchBetMessage,
                    betMessage: state.prematchBetMessage,
                    activeBetType: 'prematch',
                    backendMessage: '',
                    showStats: false,
                    showPredictions: false,
                    showTeamStats: false,
                    showRecentMatches: false,
                    showPrematchOdds: false,
                    showLiveOdds: false,
                };
            }
            return {
                ...state
            };
        case actions.LOAD_OPINION:
            {
                const hasOpinion = !action.payload.isLoading && action.payload.opinion !== undefined;
                const opinion = hasOpinion ? action.payload.opinion : state.apiMessage;
                return {
                    ...state,
                    isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                    apiMessage: action.payload.isLoading ? '' : opinion,
                    extendedAnalysisMessage: hasOpinion && action.payload.extended
                        ? opinion
                        : state.extendedAnalysisMessage,
                    backendMessage: '',
                    showStats: false,
                    showPredictions: false,
                    showTeamStats: false,
                    showRecentMatches: false,
                    showPrematchOdds: false,
                    showLiveOdds: false,
                };
            }
        case actions.LOAD_PREDICTIONS:
            return {
                ...state,
                isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                predictions: action.payload.predictions || null,
                backendMessage: '',
                showStats: false,
                showPredictions: true,
                showTeamStats: false,
                showRecentMatches: false,
                showPrematchOdds: false,
                showLiveOdds: false,
                apiMessage: '',
            };
        case actions.LOAD_PREMATCH_ODDS:
            return {
                ...state,
                isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                prematchOdds: action.payload.odds || [],
                backendMessage: '',
                showStats: false,
                showPredictions: false,
                showTeamStats: false,
                showRecentMatches: false,
                showPrematchOdds: true,
                showLiveOdds: false,
                apiMessage: '',
                selectedMarket: action.payload.selectedMarket || state.selectedMarket, // Зберігаємо або оновлюємо selectedMarket
            };
        case actions.LOAD_LIVE_ODDS:
            return {
                ...state,
                isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                liveOdds: action.payload.odds || [],
                backendMessage: '',
                showStats: false,
                showPredictions: false,
                showTeamStats: false,
                showRecentMatches: false,
                showPrematchOdds: false,
                showLiveOdds: true,
                apiMessage: '',
                selectedMarket: action.payload.selectedMarket || state.selectedMarket, // Зберігаємо або оновлюємо selectedMarket
            };
        case actions.LOAD_TEAM_STATS:
            return {
                ...state,
                isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                teamStats: action.payload.teamStats || null,
                backendMessage: '',
                showStats: false,
                showPredictions: false,
                showTeamStats: true,
                showRecentMatches: false,
                showPrematchOdds: false,
                showLiveOdds: false,
                apiMessage: '',
            };
        case actions.LOAD_RECENT_MATCHES:
            return {
                ...state,
                isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                recentMatches: action.payload.recentMatches || null,
                backendMessage: '',
                showStats: false,
                showPredictions: false,
                showTeamStats: false,
                showRecentMatches: true,
                showPrematchOdds: false,
                showLiveOdds: false,
                apiMessage: '',
            };
        case actions.LOAD_MATCH_STATS:
            return {
                ...state,
                isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                matchStats: action.payload.matchStats || null,
                backendMessage: action.payload.backendMessage || '',
                showStats: true,
                showPredictions: false,
                showTeamStats: false,
                showRecentMatches: false,
                showPrematchOdds: false,
                showLiveOdds: false,
                apiMessage: '',
            };
        case actions.SHOW_STATS:
            return {
                ...state,
                showStats: true,
                apiMessage: state.matchStats ? '' : state.apiMessage,
            };
        case actions.SET_ERROR:
            return {
                ...state,
                isLoading: action.payload.isLoading !== undefined ? action.payload.isLoading : state.isLoading,
                backendMessage: action.payload.error,
            };
        case actions.SET_SELECTED_MARKET:
            return {
                ...state,
                selectedMarket: action.payload.selectedMarket || 'all',
            };
        case actions.SET_TEAM_STATS_CACHE:
            return {
                ...state,
                teamStats: action.payload.teamStats || state.teamStats,
            };
        case 'SET_SUPPRESS_UNTIL':
            return {
                ...state,
                suppressUntil: action.payload, // число (Date.now() + X)
            };

        default:
            return state;
    }
};

function MatchPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const {user} = useUser();
    const [state, dispatch] = useReducer(reducer, initialState);

    const homeTeam = searchParams.get('home_team') || 'Невідома команда';
    const awayTeam = searchParams.get('away_team') || 'Невідома команда';
    const homeLogo = searchParams.get('home_logo') || placeholderLogo;
    const awayLogo = searchParams.get('away_logo') || placeholderLogo;
    const country = searchParams.get('country') || 'UNKNOWN';
    const date = searchParams.get('date') || '';
    const matchName = `${homeTeam} vs ${awayTeam}`;
    const status = (searchParams.get('status') || 'NS').toUpperCase();
    const score = searchParams.get('score') || '';
    const time = searchParams.get('time') || '';
    const fixtureId = searchParams.get('fixture_id') || '';
    const elapsed = searchParams.get('elapsed') && searchParams.get('elapsed') !== '' ? Number(searchParams.get('elapsed')) : null;
    const isMatchFinished = ['FT', 'ABD', 'CANC', 'SUSP', 'AWD', 'PST', 'AET', 'PEN'].includes(status);
    const [liveQuota, setLiveQuota] = useState(null);
    const [liveQuotaMsg, setLiveQuotaMsg] = useState('');
    const liveQuotaRef = useRef({last: 0, inflight: false}); // throttle + in-flight guard
    const [activeTab, setActiveTab] = useState('odds');
    const [isDemoMode] = useState(() => isDemoModeEnabled());
    const [demoStatus, setDemoStatus] = useState(null);
    const demoActive = isDemoMode && !user?.has_access;
    const isAdminUser = String(user?.role || '').toLowerCase() === 'admin';
    const [expandedTeamStats, setExpandedTeamStats] = useState({
        home: false,
        away: false,
    });
    const [favoriteTeams, setFavoriteTeams] = useState({
        home: false,
        away: false,
    });
    const [favoriteTeamsLoading, setFavoriteTeamsLoading] = useState(false);
    const [liveWatchStatus, setLiveWatchStatus] = useState({
        is_watching: false,
        is_ready: false,
        notified_live_ready: false,
        reason: '',
        readiness_checked: false,
    });
    const [liveWatchLoading, setLiveWatchLoading] = useState(false);
    const [liveWatchMessage, setLiveWatchMessage] = useState('');
    const [shareStatus, setShareStatus] = useState('');
    const [isBetGenerating, setIsBetGenerating] = useState(false);
    const [generationProgress, setGenerationProgress] = useState(0);
    const [generationCompleted, setGenerationCompleted] = useState(false);
    const [generationMode, setGenerationMode] = useState('prematch');
    const [inventoryFeatures, setInventoryFeatures] = useState({
        extended_match_analysis: false,
        extended_match_analysis_active: false,
        extended_match_analysis_cached: false,
        extra_live_tickets_available: 0,
    });
    const [analysisAvailability, setAnalysisAvailability] = useState(DEFAULT_ANALYSIS_AVAILABILITY);

    const lastLivePrefetchDone = useRef(false);
    const favoriteRequestRef = useRef(false);
    const liveWatchRequestRef = useRef(false);
    const statsPrefetchedRef = useRef(false);
    const shouldPlayReadySoundRef = useRef(false);
    const generationAudioRef = useRef(null);
    const preloadedGenerationSoundsRef = useRef([]);
    const preloadedReadySoundRef = useRef(null);
    const generationProgressRef = useRef(0);
    const generationProgressFrameRef = useRef(null);
    const generationResetTimerRef = useRef(null);

    useEffect(() => {
        generationProgressRef.current = generationProgress;
    }, [generationProgress]);

    const clearGenerationProgressFrame = useCallback(() => {
        if (generationProgressFrameRef.current) {
            window.cancelAnimationFrame(generationProgressFrameRef.current);
            generationProgressFrameRef.current = null;
        }
    }, []);

    const clearGenerationResetTimer = useCallback(() => {
        if (generationResetTimerRef.current) {
            window.clearTimeout(generationResetTimerRef.current);
            generationResetTimerRef.current = null;
        }
    }, []);

    const setGenerationProgressValue = useCallback((value) => {
        const next = Math.max(0, Math.min(100, Number(value) || 0));
        generationProgressRef.current = next;
        setGenerationProgress(next);
    }, []);

    const animateGenerationProgress = useCallback(({to = 100, duration = 1600, easing = 'easeOut'} = {}) => new Promise((resolve) => {
        clearGenerationProgressFrame();
        const startedAt = performance.now();
        const startValue = generationProgressRef.current || 0;
        const endValue = Math.max(0, Math.min(100, Number(to) || 0));

        if (startValue >= endValue) {
            setGenerationProgressValue(endValue);
            resolve();
            return;
        }

        const step = (now) => {
            const progress = Math.min(1, (now - startedAt) / duration);
            const eased = easing === 'linear' ? progress : 1 - Math.pow(1 - progress, 2);
            const nextValue = startValue + ((endValue - startValue) * eased);

            setGenerationProgressValue(nextValue);

            if (progress < 1) {
                generationProgressFrameRef.current = window.requestAnimationFrame(step);
                return;
            }

            generationProgressFrameRef.current = null;
            setGenerationProgressValue(endValue);
            resolve();
        };

        generationProgressFrameRef.current = window.requestAnimationFrame(step);
    }), [clearGenerationProgressFrame, setGenerationProgressValue]);

    useEffect(() => () => {
        clearGenerationProgressFrame();
        clearGenerationResetTimer();
    }, [clearGenerationProgressFrame, clearGenerationResetTimer]);

    useEffect(() => {
        preloadedGenerationSoundsRef.current = getGenerationSoundSources().map((src) => {
            const element = new Audio(src);
            element.loop = true;
            element.volume = getGenerationSoundVolume();
            element.preload = 'auto';
            try {
                element.load?.();
            } catch (_) {
            }
            return element;
        });
        preloadedReadySoundRef.current = createGenerationReadySoundElement();
        try {
            preloadedReadySoundRef.current.load?.();
        } catch (_) {
        }

        return () => {
            preloadedGenerationSoundsRef.current.forEach((element) => {
                try {
                    element.pause();
                    element.removeAttribute('src');
                    element.load?.();
                } catch (_) {
                }
            });
            preloadedGenerationSoundsRef.current = [];
            try {
                preloadedReadySoundRef.current?.pause();
                preloadedReadySoundRef.current?.removeAttribute('src');
                preloadedReadySoundRef.current?.load?.();
            } catch (_) {
            }
            preloadedReadySoundRef.current = null;
        };
    }, []);

    const stopGenerationSound = useCallback((immediate = false) => {
        const audio = generationAudioRef.current;
        if (!audio) return;
        generationAudioRef.current = null;

        try {
            if (audio.element) {
                if (immediate) {
                    audio.element.pause();
                    audio.element.currentTime = 0;
                    audio.element.volume = getGenerationSoundVolume();
                } else {
                    fadeOutGenerationSoundElement(audio.element, audio.keepElementSource);
                }
            }
        } catch (_) {
        }

        try {
            if (!audio.ctx) return;
            const now = audio.ctx.currentTime;
            audio.gain.gain.cancelScheduledValues(now);
            audio.gain.gain.setValueAtTime(audio.gain.gain.value || 0.0001, now);
            audio.gain.gain.exponentialRampToValueAtTime(0.0001, now + (immediate ? 0.01 : 0.18));
            window.setTimeout(() => {
                try {
                    audio.oscillators?.forEach((osc) => osc.stop());
                    audio.ctx.close();
                } catch (_) {
                }
            }, immediate ? 25 : 220);
        } catch (_) {
            try {
                audio.oscillators?.forEach((osc) => osc.stop());
                audio.ctx?.close();
            } catch (_) {
            }
        }
    }, []);

    const startGenerationSound = useCallback(() => {
        if (generationAudioRef.current) return;
        if (!isGenerationSoundEnabled()) return;
        const volume = getGenerationSoundVolume();
        if (volume <= 0) return;
        const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
        let audioState = null;

        try {
            const preloaded = preloadedGenerationSoundsRef.current
                .filter((element) => element && element.src);
            const element = preloaded.length
                ? preloaded[Math.floor(Math.random() * preloaded.length)]
                : createGenerationSoundElement();
            element.pause();
            element.currentTime = 0;
            element.loop = true;
            element.volume = volume;
            audioState = {element, keepElementSource: preloaded.includes(element)};
            generationAudioRef.current = audioState;
            const playPromise = element.play();
            if (playPromise?.catch) {
                playPromise.catch((error) => {
                    console.debug('Generation HTML sound unavailable:', error?.message || error);
                });
            }
        } catch (error) {
            console.debug('Generation HTML sound init failed:', error?.message || error);
        }

        try {
            if (!AudioContextCtor) return;
            const ctx = new AudioContextCtor();
            const gain = ctx.createGain();
            const filter = ctx.createBiquadFilter();
            const pulse = ctx.createOscillator();
            const shimmer = ctx.createOscillator();

            filter.type = 'lowpass';
            filter.frequency.setValueAtTime(720, ctx.currentTime);
            filter.Q.setValueAtTime(0.7, ctx.currentTime);

            pulse.type = 'sine';
            pulse.frequency.setValueAtTime(132, ctx.currentTime);
            shimmer.type = 'triangle';
            shimmer.frequency.setValueAtTime(264, ctx.currentTime);

            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.008 * volume, ctx.currentTime + 0.12);

            pulse.connect(filter);
            shimmer.connect(filter);
            filter.connect(gain);
            gain.connect(ctx.destination);

            pulse.start();
            shimmer.start();
            generationAudioRef.current = {
                ...(audioState || {}),
                ctx,
                gain,
                oscillators: [pulse, shimmer],
            };
            ctx.resume?.();
        } catch (error) {
            if (!audioState) {
                generationAudioRef.current = null;
            }
            console.debug('Generation sound unavailable:', error?.message || error);
        }
    }, []);

    const kickGenerationSound = useCallback(() => {
        startGenerationSound();
        window.setTimeout(() => {
            generationAudioRef.current?.ctx?.resume?.();
        }, 0);
    }, [startGenerationSound]);

    useEffect(() => () => stopGenerationSound(), [stopGenerationSound]);

    const elapsedNum = Number(elapsed ?? 0) || 0;
    const notStartedStatuses = new Set(['NS', 'TBD']);
    const liveStatuses = new Set(['LIVE', '1H', '2H', 'HT', 'ET', 'P']);
    const isNotStarted = notStartedStatuses.has(status);
    const isLiveStarted = liveStatuses.has(status);
    const canLiveByTime = isLiveStarted && elapsedNum >= 25 && elapsedNum <= 75;
    const hasStatsForLive = !!(state.matchStats?.home_stats && state.matchStats?.away_stats);
    const generationProgressValue = Math.max(0, Math.min(100, Number(generationProgress) || 0));
    const generationProgressLabel = generationProgressValue >= 99.95
        ? 100
        : Math.floor(generationProgressValue);
    const generationProgressStyle = {
        transform: `scaleX(${(generationProgressValue / 100).toFixed(5)})`,
    };

    const getActiveTelegramId = useCallback(() => (
        state.user?.telegram_id
        || user?.telegram_id
        || window.Telegram?.WebApp?.initDataUnsafe?.user?.id
        || localStorage.getItem('telegram_id')
    ), [state.user?.telegram_id, user?.telegram_id]);

    const getBetViewParams = useCallback((type = 'prematch') => ({
        telegramId: getActiveTelegramId(),
        type,
        matchName,
        date,
        fixtureId,
    }), [getActiveTelegramId, matchName, date, fixtureId]);

    const hasSeenThisMatchBet = useCallback((type = 'prematch') => (
        hasUserSeenMatchBet(getBetViewParams(type))
    ), [getBetViewParams]);

    const markSeenThisMatchBet = useCallback((type = 'prematch') => {
        markUserSeenMatchBet(getBetViewParams(type));
    }, [getBetViewParams]);

    const fetchSeenMatchBetFromHistory = useCallback(async (type = 'prematch') => {
        const localSeen = hasSeenThisMatchBet(type);
        const telegramId = getActiveTelegramId();
        if (!telegramId) return {found: false, localSeen};

        try {
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const params = new URLSearchParams({
                match: matchName,
                date,
                bet_type: type,
            });
            if (fixtureId) {
                params.set('fixture_id', String(fixtureId));
            }
            params.set('_', String(Date.now()));
            const res = await fetch(
                `${API_BASE}/user/${encodeURIComponent(String(telegramId))}/bets/last_for_match?${params.toString()}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache',
                    },
                    cache: 'no-store',
                }
            );
            if (!res.ok) return {found: false, localSeen};
            const data = await res.json();
            if (data?.found) {
                return {found: true, data, localSeen};
            }
            return {found: false, localSeen};
        } catch (error) {
            console.debug('Could not check bet history:', error?.message || error);
            return {found: false, localSeen};
        }
    }, [hasSeenThisMatchBet, getActiveTelegramId, matchName, date, fixtureId]);

    const playGenerationDoneFeedback = useCallback(() => {
        stopGenerationSound(true);
        if (shouldPlayReadySoundRef.current) {
            shouldPlayReadySoundRef.current = false;
            playGenerationReadySound(preloadedReadySoundRef.current);
        }

        const hapticsEnabled = localStorage.getItem('grachHapticsEnabled') !== 'false';
        if (hapticsEnabled) {
            const haptics = window.Telegram?.WebApp?.HapticFeedback;
            if (haptics?.notificationOccurred) {
                haptics.notificationOccurred('success');
            } else if (navigator.vibrate) {
                navigator.vibrate([55, 35, 90]);
            }
        }
    }, [stopGenerationSound]);

    const silenceGenerationFeedback = useCallback(() => {
        shouldPlayReadySoundRef.current = false;
        clearGenerationProgressFrame();
        clearGenerationResetTimer();
        stopGenerationSound(true);
        setIsBetGenerating(false);
        setGenerationCompleted(false);
        setGenerationProgressValue(0);
        try {
            preloadedReadySoundRef.current?.pause();
            preloadedReadySoundRef.current.currentTime = 0;
        } catch (_) {
        }
    }, [stopGenerationSound, clearGenerationProgressFrame, clearGenerationResetTimer, setGenerationProgressValue]);

    const createDelayedGenerationFeedback = useCallback((enabled = true, options = {}) => {
        let started = false;
        let completed = false;
        let stopped = false;
        let timer = null;
        let progressMode = 'real';
        let progressStartedAt = 0;
        let uiStartedAt = 0;
        const startDelay = Math.max(0, Number(options.startDelay ?? 1400) || 0);

        const runProgressLoop = () => {
            clearGenerationProgressFrame();
            progressStartedAt = performance.now();

            const tick = (now) => {
                if (stopped || !started) return;
                if (progressMode === 'manual') return;

                const target = getGenerationProgressTarget(now - progressStartedAt);
                const next = Math.max(generationProgressRef.current || 0, Math.min(96, target));
                setGenerationProgressValue(next);

                if (next < 96) {
                    generationProgressFrameRef.current = window.requestAnimationFrame(tick);
                } else {
                    generationProgressFrameRef.current = null;
                }
            };

            generationProgressFrameRef.current = window.requestAnimationFrame(tick);
        };

        const start = (mode = 'real') => {
            if (!enabled) return false;
            const isFirstStart = !started;
            started = true;
            progressMode = mode;
            clearGenerationProgressFrame();
            if (isFirstStart) {
                uiStartedAt = Date.now();
                clearGenerationResetTimer();
                setGenerationCompleted(false);
                setGenerationProgressValue(0);
                setIsBetGenerating(true);
                shouldPlayReadySoundRef.current = true;
                kickGenerationSound();
            }
            if (mode === 'real') {
                runProgressLoop();
            }
            return true;
        };

        if (enabled) {
            if (startDelay === 0) {
                start();
            } else {
                timer = window.setTimeout(start, startDelay);
            }
        }

        return {
            cancel: () => {
                if (timer) {
                    window.clearTimeout(timer);
                    timer = null;
                }
                if (!started) {
                    stopped = true;
                }
            },
            finishWithMinimumDelay: async () => {
                if (!enabled) return;
                if (timer) {
                    window.clearTimeout(timer);
                    timer = null;
                }
                start('real');
                await delay(Math.max(0, FIRST_MATCH_BET_LOADING_MS - (Date.now() - uiStartedAt)));
                progressMode = 'manual';
                clearGenerationProgressFrame();
                await animateGenerationProgress({to: 100, duration: 1400, easing: 'easeOut'});
                completed = true;
                await delay(450);
            },
            finishWithFakeFullRun: async () => {
                if (!enabled) return;
                if (timer) {
                    window.clearTimeout(timer);
                    timer = null;
                }
                start('manual');
                progressMode = 'manual';
                clearGenerationProgressFrame();
                const currentProgress = generationProgressRef.current || 0;
                const remainingRatio = Math.max(0, 100 - currentProgress) / 100;
                const duration = Math.max(900, CACHED_MATCH_BET_FAKE_LOADING_MS * remainingRatio);
                await animateGenerationProgress({to: 100, duration, easing: 'linear'});
                completed = true;
                await delay(450);
            },
            stop: () => {
                if (stopped) return;
                stopped = true;
                if (timer) {
                    window.clearTimeout(timer);
                    timer = null;
                }
                clearGenerationProgressFrame();
                if (started) {
                    if (completed) {
                        setGenerationProgressValue(100);
                        setGenerationCompleted(true);
                        playGenerationDoneFeedback();
                        clearGenerationResetTimer();
                        generationResetTimerRef.current = window.setTimeout(() => {
                            setGenerationCompleted(false);
                            setGenerationProgressValue(0);
                            generationResetTimerRef.current = null;
                        }, 1500);
                    } else {
                        shouldPlayReadySoundRef.current = false;
                        setGenerationCompleted(false);
                        setGenerationProgressValue(0);
                        stopGenerationSound(true);
                    }
                    setIsBetGenerating(false);
                }
            },
        };
    }, [
        animateGenerationProgress,
        clearGenerationProgressFrame,
        clearGenerationResetTimer,
        kickGenerationSound,
        playGenerationDoneFeedback,
        setGenerationProgressValue,
        stopGenerationSound,
    ]);

    const getFormArray = (form) => {
        if (!form || typeof form !== 'string') return [];
        return form
            .split('')
            .map((x) => x.trim().toUpperCase())
            .filter(Boolean)
            .slice(-5);
    };

    const cleanInfoText = (value) => String(value || '')
        .replace(/\*\*/g, '')
        .replace(/^[-–—\s]+/gm, '')
        .trim();

    const splitOpinionParagraphs = (value) => {
        const paragraphs = cleanInfoText(value)
            .split(/\n{2,}|\n(?=[А-ЯІЇЄҐA-Z])/)
            .map((part) => part.trim())
            .filter(Boolean);

        if (paragraphs.length <= 1 && paragraphs[0]?.length > 260) {
            const sentences = paragraphs[0]
                .split(/(?<=[.!?])\s+(?=[А-ЯІЇЄҐA-Z])/)
                .map((part) => part.trim())
                .filter(Boolean);
            const chunks = [];
            for (let i = 0; i < sentences.length; i += 2) {
                chunks.push(sentences.slice(i, i + 2).join(' '));
            }
            return chunks.slice(0, 6);
        }

        return paragraphs.slice(0, 6);
    };

    const parseInfoSections = (text) => {
        const raw = cleanInfoText(text);
        if (!raw) return null;

        const altIdx = raw.indexOf('Альтернатива:');
        const motivationIdx = raw.indexOf('Мотивація:');
        const whyIdx = raw.indexOf('Чому:');
        const confidenceIdx = raw.indexOf('Надійність:');

        const hasStructuredMarkers = [altIdx, motivationIdx, whyIdx, confidenceIdx].some((idx) => idx >= 0);
        const looksLikeBetBlock = /✅|❌|NO BET|кеф|ставка/i.test(raw);

        if (!hasStructuredMarkers && !looksLikeBetBlock) {
            return {
                mode: 'opinion',
                opinionParagraphs: splitOpinionParagraphs(raw),
                raw,
            };
        }

        const firstCutCandidates = [altIdx, motivationIdx, whyIdx, confidenceIdx].filter((v) => v >= 0);
        const firstCut = firstCutCandidates.length ? Math.min(...firstCutCandidates) : -1;

        const nextMarkerAfter = (startIdx) => {
            const candidates = [altIdx, motivationIdx, whyIdx, confidenceIdx]
                .filter((idx) => idx >= 0 && idx > startIdx);
            return candidates.length ? Math.min(...candidates) : raw.length;
        };

        const mainBet = cleanInfoText(firstCut >= 0 ? raw.slice(0, firstCut) : raw);
        const alternative = altIdx >= 0
            ? cleanInfoText(raw.slice(
                altIdx + 'Альтернатива:'.length,
                nextMarkerAfter(altIdx)
            ))
            : '';
        const motivation = motivationIdx >= 0
            ? cleanInfoText(raw.slice(
                motivationIdx + 'Мотивація:'.length,
                nextMarkerAfter(motivationIdx)
            ))
            : '';
        const why = whyIdx >= 0
            ? cleanInfoText(raw.slice(
                whyIdx + 'Чому:'.length,
                nextMarkerAfter(whyIdx)
            ))
            : '';
        const confidence = confidenceIdx >= 0
            ? cleanInfoText(raw.slice(confidenceIdx + 'Надійність:'.length, nextMarkerAfter(confidenceIdx)))
            : '';

        return {mode: 'bet', mainBet, alternative, motivation, why, confidence, raw};
    };

    const parsePremiumValueScan = (text) => {
        const raw = cleanInfoText(text);
        if (!raw || !/PREMIUM_VALUE_SCAN|Оцінка проходу:|Чесний кеф:|Value:/i.test(raw)) return null;

        const labels = [
            'Матч-профіль:',
            'Сценарій:',
            'Радар ринків:',
            'Value-карта:',
            'Факти:',
            'Ризики:',
            'Що може зламати ставку:',
            'Перед матчем перевірити:',
            'Live-план:',
            'Фінал:',
            'Основна ставка:',
            'Оцінка проходу:',
            'Кеф лінії:',
            'Чесний кеф:',
            'Value:',
            'Надійність:',
            'Альтернативи:',
        ];

        const take = (label) => {
            const start = raw.indexOf(label);
            if (start < 0) return '';
            const contentStart = start + label.length;
            const nextIndexes = labels
                .filter((item) => item !== label)
                .map((item) => raw.indexOf(item, contentStart))
                .filter((idx) => idx >= 0);
            const end = nextIndexes.length ? Math.min(...nextIndexes) : raw.length;
            return cleanInfoText(raw.slice(contentStart, end)
                .replace(/PREMIUM_VALUE_SCAN/g, '')
                .replace(/---\s*АНАЛІТИЧНА ДУМКА\s*---/gi, '')
                .replace(/---\s*НАЙКРАЩА СТАВКА\s*---/gi, ''));
        };

        const takeFirst = (...labelOptions) => {
            for (const label of labelOptions) {
                const value = take(label);
                if (value) return value;
            }
            return '';
        };

        const splitList = (value) => {
            const normalized = cleanInfoText(value)
                .replace(/\r/g, '\n')
                .replace(/(^|\n)\s*([•*])\s+/g, '\n• ')
                .replace(/(^|\n)\s*([1-9][.)])\s+/g, '\n$2 ');

            const items = [];
            normalized
                .split(/\n+/)
                .map((line) => line.trim())
                .filter(Boolean)
                .forEach((line) => {
                    const startsNewItem = /^([•*]|[1-9][.)])\s+/.test(line);
                    const text = line.replace(/^([•*]|[1-9][.)])\s+/, '').trim();
                    if (!text) return;
                    if (startsNewItem || !items.length) {
                        items.push(text);
                    } else {
                        items[items.length - 1] = `${items[items.length - 1]} ${text}`;
                    }
                });

            return items.slice(0, 6);
        };

        const probability = take('Оцінка проходу:');
        const probabilityNumber = Number((probability.match(/\d+(?:[.,]\d+)?/) || ['0'])[0].replace(',', '.'));
        const mainBet = take('Основна ставка:');
        const noBet = /NO BET/i.test(raw) && (!mainBet || /NO BET/i.test(mainBet));

        return {
            mode: 'premium',
            raw,
            noBet,
            matchProfile: take('Матч-профіль:'),
            scenario: take('Сценарій:'),
            marketRadar: splitList(take('Радар ринків:')),
            valueMap: splitList(take('Value-карта:')),
            facts: splitList(take('Факти:')),
            risks: splitList(takeFirst('Що може зламати ставку:', 'Ризики:')),
            checklist: splitList(take('Перед матчем перевірити:')),
            livePlan: splitList(take('Live-план:')),
            final: take('Фінал:'),
            mainBet,
            probability,
            probabilityNumber: Number.isFinite(probabilityNumber) ? Math.max(0, Math.min(100, probabilityNumber)) : 0,
            lineOdds: take('Кеф лінії:'),
            fairOdds: take('Чесний кеф:'),
            value: take('Value:'),
            confidence: take('Надійність:'),
            alternatives: splitList(take('Альтернативи:')),
        };
    };

    const renderCompactForm = (form) => {
        const formArray = getFormArray(form);
        const items = formArray.length ? formArray : ['-', '-', '-', '-', '-'];

        return (
            <div className={styles.compactFormRow}>
                {items.map((result, index) => {
                    const normalized = String(result).toUpperCase();
                    const isPlaceholder = normalized === '-';

                    const cls = isPlaceholder
                        ? styles.compactFormPlaceholder
                        : normalized === 'W'
                            ? styles.compactFormWin
                            : normalized === 'D'
                                ? styles.compactFormDraw
                                : normalized === 'L'
                                    ? styles.compactFormLose
                                    : '';

                    return (
                        <span
                            key={`${normalized}-${index}`}

                            className={`${styles.compactFormBadge} ${cls}`}
                        >
                        {isPlaceholder ? '' : normalized}
                    </span>
                    );
                })}
            </div>
        );
    };


    const gradientMap = {
        'ESP': ['rgb(225,102,102)', 'rgba(223,223,112,0.6)', 'rgba(230,117,81,0.8)', 'rgba(243,216,54,0.79)'],
        'ENG': ['#FFFFFF', '#ae432b', '#ffffff', '#cf4d4d'],
        'ITA': ['#84d15d', 'rgba(255,255,255,0.67)', '#ba3e3e', '#ba6a6a'],
        'GER': ['#4a3e3e', '#c14141', '#d3bf71', '#d84a4a'],
        'UEFA': ['#0000FF', '#FFFFFF', '#4169E1', '#F0F8FF'],
        'UA': ['#d3c146', '#4690cd', '#b8b132', '#4390cf'],
        'UNKNOWN': ['#1a1a1a', '#4a4a4a', '#2a2a2a', '#3a3a3a'],
    };

    // const gradientColors = useMemo(() => {
    //     return gradientMap[country.toUpperCase()] || gradientMap['UNKNOWN'];
    // }, [country]);
    //
    // useEffect(() => {
    //     document.documentElement.style.setProperty('--gradient-color-1', gradientColors[0] || '#1a1a1a');
    //     document.documentElement.style.setProperty('--gradient-color-2', gradientColors[1] || '#4a4a4a');
    //     document.documentElement.style.setProperty('--gradient-color-3', gradientColors[2] || '#2a2a2a');
    //     document.documentElement.style.setProperty('--gradient-color-4', gradientColors[3] || '#3a3a3a');
    // }, [gradientColors]);


    useEffect(() => {
        if (window.Telegram?.WebApp) {
            window.Telegram.WebApp.ready();
            window.Telegram.WebApp.expand();
            window.Telegram.WebApp.disableVerticalSwipes();
        }
        if (isLiveStarted || isMatchFinished) {
            matchStatsButtonClick();
        }
    }, []);


    useEffect(() => {
        let cancelled = false;

        const preloadTeamStats = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const res = await fetch(
                    `${API_BASE}/get_team_statistics?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}&last_n=10`
                );
                if (!res.ok) return;

                const data = await res.json();
                if (cancelled || !data) return;

                dispatch({
                    type: actions.SET_TEAM_STATS_CACHE,
                    payload: {teamStats: data},
                });
            } catch {
            }
        };

        preloadTeamStats();

        return () => {
            cancelled = true;
        };
    }, [matchName, date]);

    useEffect(() => {
        let cancelled = false;

        const loadAnalysisAvailability = async () => {
            if (!matchName || !date || isMatchFinished) {
                setAnalysisAvailability({
                    ...DEFAULT_ANALYSIS_AVAILABILITY,
                    loading: false,
                    checked: true,
                    can_get_bet: !isMatchFinished,
                    can_get_extended: !isMatchFinished,
                    reason: isMatchFinished ? 'Матч завершено, ставки недоступні.' : '',
                });
                return;
            }

            setAnalysisAvailability((prev) => ({...prev, loading: true}));
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const res = await fetch(
                    `${API_BASE}/match_analysis_availability?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}`,
                    {headers: {'Cache-Control': 'no-cache'}}
                );
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (cancelled) return;
                setAnalysisAvailability({
                    ...DEFAULT_ANALYSIS_AVAILABILITY,
                    ...data,
                    loading: false,
                    checked: true,
                    can_get_bet: data.can_get_bet !== false,
                    can_get_extended: data.can_get_extended !== false,
                    reason: data.reason || '',
                    simple_warning: data.simple_warning || '',
                });
            } catch (error) {
                console.error('Помилка перевірки доступності аналізу:', error.message);
                if (!cancelled) {
                    setAnalysisAvailability({
                        ...DEFAULT_ANALYSIS_AVAILABILITY,
                        loading: false,
                        checked: true,
                        can_get_bet: false,
                        can_get_extended: false,
                        reason: 'Не вдалося перевірити дані матчу. Спробуй оновити сторінку або зайти трохи пізніше.',
                    });
                }
            }
        };

        loadAnalysisAvailability();
        return () => {
            cancelled = true;
        };
    }, [matchName, date, isMatchFinished]);


    useEffect(() => {
        // статус уже є у тебе в змінній `status`
        const FINISHED_OR_INTERRUPTED = new Set([
            'FT', 'AET', 'PEN', 'ABD', 'SUSP', 'CANC', 'PST', 'PPD', 'AWD', 'WO', 'INT'
        ]);

        if (!FINISHED_OR_INTERRUPTED.has(status)) return;
        if (statsPrefetchedRef.current) return;

        statsPrefetchedRef.current = true;
        matchStatsButtonClick(); // авто-показ тільки коли матч завершено/перервано
    }, [status]);



    useEffect(() => {
        // перший показ квоти без throttle
        fetchLiveQuota(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        // умова, коли ця інфа дійсно потрібна (кнопка LIVE може з’явитись)
        const shouldFetch =
            !isMatchFinished &&
            (elapsed > 0 || (state.matchStats?.home_stats && state.matchStats?.away_stats));

        if (!shouldFetch) return;

        const now = Date.now();
        // не частіше ніж раз на 15с
        if (now - liveQuotaRef.current.last < 15000) return;
        if (liveQuotaRef.current.inflight) return;

        liveQuotaRef.current.inflight = true;
        fetchLiveQuota().finally(() => {
            liveQuotaRef.current.inflight = false;
            liveQuotaRef.current.last = Date.now();
        });
    }, [isMatchFinished, elapsed, state.matchStats]);


    const marketMap = {
        'Match Winner': 'Основне',
        'Total Goals': 'Тотал',
        'Handicap': 'Фора',
        'Half Time': 'Тайми',
        'Player to Score': 'Гравці',
        'Corners': 'Кутові',
        'Correct Score': 'Голи/Рахунки',
        'GOAL OVER/UNDER FIRST HALF': 'Тотал 1-й тайм',
        'GOAL OVER/UNDER - SECOND HALF': 'Тотал 2-й тайм',
        'Home Team': 'Господарі',
        'Away Team': 'Гості',
        'Combo': 'Комбо',
        'Cards / Penalty': 'Картки/Пенальті',
        'Shots': 'Удари',
    };

    const marketNameMapUa = {
        'Match Winner': 'Переможець матчу',
        'Total Goals': 'Більше / Менше',
        'Handicap': 'Азіатський гандикап',
        'SECOND HALF WINNER': '"2-й тайм переможець"',
        'Half Time': '1-й тайм',
        'Player to Score': 'Гравець забʼє',
        'Corners': 'Кутові',
        'Correct Score': 'Точний рахунок',
        'Home Team': 'Господарі',
        'Away Team': 'Гості',
        'Combo': 'Комбіновані ставки',
        'Cards / Penalty': 'Картки / Пенальті',
        'Shots': 'Удари',
        'Both Teams Score': 'Обидві забʼють',
        'Double Chance': 'Подвійний шанс',
        'Draw No Bet': 'Нічия — ставки повертаються',
        'Asian Handicap': 'Азіатський гандикап',
        'Goals Over/Under': 'Більше / Менше',
        'Team To Score': 'Команда забʼє',
        'Odd/Even': 'Парна / Непарна',
    };

    const translateMarketName = (market) => {
        if (!market) return 'Ринок';
        return marketNameMapUa[market] || marketMap[market] || market;
    };

    const translateBetValue = (value, market) => {
        if (value === null || value === undefined) return '';
        const raw = String(value).trim();
        const normalizedMarket = String(market || '').trim();
        const lowerMarket = normalizedMarket.toLowerCase();
        const upper = raw.toUpperCase();

        if (raw === '1') return 'Перемога господарів';
        if (upper === 'X' || upper === 'DRAW') return 'Нічия';
        if (raw === '2') return 'Перемога гостей';
        if (upper === 'HOME') return 'Господарі';
        if (upper === 'AWAY') return 'Гості';
        if (upper === '1X') return '1X — господарі або нічия';
        if (upper === '12') return '12 — без нічиєї';
        if (upper === 'X2') return 'X2 — гості або нічия';
        if (upper === 'YES') return 'Так';
        if (upper === 'NO') return 'Ні';
        if (upper === 'ODD') return 'Непарна';
        if (upper === 'EVEN') return 'Парна';

        if (lowerMarket.includes('both teams score')) {
            if (upper === 'YES') return 'Обидві забʼють — Так';
            if (upper === 'NO') return 'Обидві забʼють — Ні';
        }

        if (lowerMarket.includes('total') || lowerMarket.includes('over/under') || lowerMarket.includes('goals over/under')) {
            if (/^over\\s+/i.test(raw)) return raw.replace(/^over\\s+/i, 'Більше ');
            if (/^under\\s+/i.test(raw)) return raw.replace(/^under\\s+/i, 'Менше ');
            if (/^over$/i.test(raw)) return 'Більше';
            if (/^under$/i.test(raw)) return 'Менше';
        }

        if (lowerMarket.includes('handicap')) {
            if (/^home\\s+/i.test(raw)) return raw.replace(/^home\\s+/i, 'Господарі ');
            if (/^away\\s+/i.test(raw)) return raw.replace(/^away\\s+/i, 'Гості ');
            if (/^home$/i.test(raw)) return 'Господарі';
            if (/^away$/i.test(raw)) return 'Гості';
        }

        if (lowerMarket.includes('home team')) {
            if (/^over\\s+/i.test(raw)) return raw.replace(/^over\\s+/i, 'Господарі більше ');
            if (/^under\\s+/i.test(raw)) return raw.replace(/^under\\s+/i, 'Господарі менше ');
            if (/^yes$/i.test(raw)) return 'Господарі забʼють — Так';
            if (/^no$/i.test(raw)) return 'Господарі забʼють — Ні';
        }

        if (lowerMarket.includes('away team')) {
            if (/^over\\s+/i.test(raw)) return raw.replace(/^over\\s+/i, 'Гості більше ');
            if (/^under\\s+/i.test(raw)) return raw.replace(/^under\\s+/i, 'Гості менше ');
            if (/^yes$/i.test(raw)) return 'Гості забʼють — Так';
            if (/^no$/i.test(raw)) return 'Гості забʼють — Ні';
        }

        if (raw.includes('/')) {
            const parts = raw.split('/').map((part) => part.trim()).filter(Boolean);
            if (parts.length > 0) {
                return parts
                    .map((part) => translateBetValue(part, market))
                    .join(' / ');
            }
        }

        return raw;
    };

    const saveBet = async (match, bet_type, bet_value) => {
        if (!user || !user.telegram_id) {
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: 'Помилка: користувач не авторизований', isLoading: false}
            });
            return false;
        }
        try {
            const body = JSON.stringify({
                match,
                bet_type,
                bet_value,
                home_team: homeTeam,
                away_team: awayTeam,
                country,
                home_logo: homeLogo,
                away_logo: awayLogo,
                date
            });
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(`${API_BASE}/user/${user.telegram_id}/add-bet`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                    'Expires': '0',
                },
                body
            });
            const responseText = await res.text();
            if (!res.ok) {
                throw new Error(`Помилка збереження ставки: HTTP ${res.status}, ${responseText}`);
            }
            const data = JSON.parse(responseText);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: `Ставка успішно збережена: ${JSON.stringify(data)}`, isLoading: false}
            });
            return true;
        } catch (error) {
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: `Помилка збереження ставки: ${error.message}`, isLoading: false}
            });
            return false;
        }
    };

    const fetchLiveQuota = async (force = false) => {
        try {
            const telegramId =
                state.user?.telegram_id ||
                window.Telegram?.WebApp?.initDataUnsafe?.user?.id ||
                localStorage.getItem('telegram_id');

            if (!telegramId) {
                setLiveQuotaMsg('Не вдалося визначити telegram_id для квоти');
                return;
            }

            // throttle — не частіше ніж раз на 15с, КРІМ випадку force=true
            if (!force) {
                const now = Date.now();
                if (now - liveQuotaRef.current.last < 15000) return;
                if (liveQuotaRef.current.inflight) return;
            }

            liveQuotaRef.current.inflight = true;
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/user/${telegramId}/live_quota`,
                {headers: {'Cache-Control': 'no-cache'}}
            );
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();

            let msg = '';
            if (data.total === null || data.remaining === 'unlimited') {
                msg = 'Без обмежень';
            } else {
                const remaining = Number(data.remaining || 0);
                if (remaining <= 0) msg = 'На сьогодні всі спроби використано';
                else if (remaining === 1) msg = 'Ще 1 LIVE ставка сьогодні';
                else msg = `Ще ${remaining} LIVE ставки сьогодні`;
            }

            setLiveQuota(data);
            setLiveQuotaMsg(msg);           // ← без setTimeout-трюків
        } catch (e) {
            setLiveQuotaMsg('Не вдалося отримати квоту LIVE');
        } finally {
            liveQuotaRef.current.inflight = false;
            liveQuotaRef.current.last = Date.now();
        }
    };

    const fetchInventoryFeatures = async () => {
        try {
            const telegramId =
                state.user?.telegram_id ||
                user?.telegram_id ||
                window.Telegram?.WebApp?.initDataUnsafe?.user?.id ||
                localStorage.getItem('telegram_id');
            if (!telegramId || demoActive) return;
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const featureParams = new URLSearchParams();
            if (matchName) featureParams.set('match', matchName);
            if (date) featureParams.set('date', date);
            const featureQuery = featureParams.toString() ? `?${featureParams.toString()}` : '';
            const res = await fetch(`${API_BASE}/user/${telegramId}/features${featureQuery}`, {
                headers: {'Cache-Control': 'no-cache'},
            });
            if (!res.ok) return;
            const data = await res.json();
            setInventoryFeatures({
                extended_match_analysis: !!data.extended_match_analysis,
                extended_match_analysis_active: !!data.extended_match_analysis_active,
                extended_match_analysis_cached: !!data.extended_match_analysis_cached,
                extra_live_tickets_available: Number(data.extra_live_tickets_available || 0),
            });
        } catch (error) {
            console.error('Помилка завантаження функцій інвентаря:', error.message);
        }
    };

    useEffect(() => {
        fetchInventoryFeatures();
    }, [user?.telegram_id, demoActive]);

    const confirmSimpleOddsWarning = async () => {
        const warning = analysisAvailability.simple_warning;
        if (!warning) return true;
        const message = `${warning}\n\nПродовжити без коефіцієнтів?`;
        const tg = window.Telegram?.WebApp;
        if (typeof tg?.showConfirm === 'function') {
            return new Promise((resolve) => {
                tg.showConfirm(message, (confirmed) => resolve(!!confirmed));
            });
        }
        if (typeof window.confirm === 'function') {
            return window.confirm(message);
        }
        return true;
    };

    const normalizePublicApiError = (message, fallback = 'Не вдалося виконати запит') => {
        const raw = String(message || '').trim();
        if (!raw) return fallback;
        const withoutPrefix = raw
            .replace(/^Помилка виклику API:\s*/i, '')
            .replace(/^HTTP\s+\d+,?\s*/i, '')
            .trim();
        let detail = withoutPrefix;
        if ((detail.startsWith('{') && detail.endsWith('}')) || (detail.startsWith('[') && detail.endsWith(']'))) {
            try {
                const parsed = JSON.parse(detail);
                detail = parsed?.detail || parsed?.message || fallback;
            } catch (_) {
                detail = fallback;
            }
        }
        const lower = String(detail || '').toLowerCase();
        if (lower.includes('live statistics not available')) {
            return 'Live-статистика для цього матчу зараз недоступна.';
        }
        if (lower.includes('match not found')) {
            return 'Матч не знайдено або він ще не оновився в базі.';
        }
        if (lower.includes('teams not found')) {
            return 'Команди для цього матчу не знайдені в базі.';
        }
        if (lower.includes('failed to get prematch odds')) {
            return 'Коефіцієнти для цього матчу зараз недоступні.';
        }
        return detail || fallback;
    };

    const readApiError = async (res, fallback = 'Не вдалося виконати запит') => {
        let detail = '';
        try {
            const data = await res.json();
            detail = data?.detail || data?.message || '';
        } catch (_) {
            try {
                detail = await res.text();
            } catch (_) {
                detail = '';
            }
        }
        const rawMessage = detail || fallback;
        if (isAdminUser) {
            return `HTTP ${res.status}: ${rawMessage}`;
        }
        return normalizePublicApiError(rawMessage, fallback);
    };

    async function fetchLastLiveBetForMatch() {
        try {
            // не оновлюємо, якщо ще діє "лок" після генерації
            if (state.suppressUntil && Date.now() < state.suppressUntil) {
                return false;
            }

            const telegramId =
                state.user?.telegram_id ||
                window.Telegram?.WebApp?.initDataUnsafe?.user?.id ||
                localStorage.getItem('telegram_id');

            if (!telegramId || !matchName || !date) return false;

            const qs = new URLSearchParams({match: matchName, date, bet_type: 'live'}).toString();
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/user/${telegramId}/bets/last_for_match?${qs}&_=${Date.now()}`, // bust cache
                {
                    headers: {
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache',
                    },
                    cache: 'no-store',
                }
            );
            if (!res.ok) return false;

            const data = await res.json();

            // Очікуємо generated_time з бекенда
            const fetchedTs = data?.generated_time || data?.generatedTime || null;

            // Якщо бекенд не дав мітку часу — НЕ перезаписуємо (щоб не приклеїти старе)
            if (!fetchedTs) return false;

            // Оновлюємо лише якщо fetchedTs новіший за те, що вже показано
            if (state.lastGeneratedAt && new Date(fetchedTs) < new Date(state.lastGeneratedAt)) {
                return false;
            }

            if (data?.found && (data.message || data.opinion)) {
                dispatch({
                    type: actions.CACHE_LIVE_BET,
                    payload: {
                        message: data.message || '',
                        matchStats: state.matchStats || null,
                        generated_time: fetchedTs,
                        isLoading: false,
                    }
                });
                return true;
            }
            return false;
        } catch {
            return false;
        }
    }


// скидаємо одноразовий прапор при зміні матчу або дати
    useEffect(() => {
        lastLivePrefetchDone.current = false;
    }, [matchName, date]);

    useEffect(() => {
        const liveNow = Number(elapsed) > 0 || (state.matchStats?.home_stats && state.matchStats?.away_stats);
        if (!liveNow) return;
        if (isMatchFinished) return;

        if (lastLivePrefetchDone.current) return;
        lastLivePrefetchDone.current = true;

        fetchLastLiveBetForMatch(); // тихо кешує останню LIVE-ставку, не перебиваючи prematch
    }, [state.matchStats, elapsed, isMatchFinished, matchName, date]);


    const liveBetButtonClick = async () => {
        if (state.isLoading) {
            console.log('liveBetButtonClick blocked: isLoading is true');
            return;
        }
        const seenLiveBet = await fetchSeenMatchBetFromHistory('live');
        if (seenLiveBet.found && (seenLiveBet.data?.message || seenLiveBet.data?.opinion)) {
            silenceGenerationFeedback();
            setActiveTab(null);
            dispatch({
                type: actions.LOAD_LIVE_BET,
                payload: {
                    message: seenLiveBet.data.message || seenLiveBet.data.opinion,
                    matchStats: state.matchStats || null,
                    generated_time: seenLiveBet.data.generated_time || null,
                    isLoading: false,
                },
            });
            window.requestAnimationFrame(() => {
                document.querySelector(`.${styles.infoPanel}`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
            return;
        }
        if (state.liveBetMessage) {
            setActiveTab(null);
            silenceGenerationFeedback();
            dispatch({
                type: actions.LOAD_LIVE_BET,
                payload: {
                    message: state.liveBetMessage,
                    matchStats: state.matchStats || null,
                    generated_time: state.liveGeneratedAt || null,
                    isLoading: false,
                },
            });
            markSeenThisMatchBet('live');
            window.requestAnimationFrame(() => {
                document.querySelector(`.${styles.infoPanel}`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
            return;
        }
        if (!canLiveByTime || !hasStatsForLive || !liveWatchStatus.is_ready) {
            dispatch({
                type: actions.SET_ERROR,
                payload: {
                    error: liveReadinessReasonText[liveWatchStatus.reason] || 'Поки немає надійного моменту для LIVE ставки, слідкую',
                    isLoading: false,
                }
            });
            return;
        }
        const firstViewForUser = !seenLiveBet.localSeen;
        setGenerationMode('live');
        const feedback = createDelayedGenerationFeedback(firstViewForUser, {startDelay: 0});
        setActiveTab(null);
        if (!firstViewForUser) {
            silenceGenerationFeedback();
        }
        dispatch({type: actions.LOAD_LIVE_BET, payload: {isLoading: true}});
        try {
            // Отримуємо telegram_id (припустимо, з Redux state)
            const telegramId = state.user?.telegram_id || window.Telegram?.WebApp?.initDataUnsafe?.user?.id || localStorage.getItem('telegram_id'); // Заглушка для тестування
            console.log(`liveBetButtonClick called: match=${matchName}, date=${date}, score=${score}, elapsed=${elapsed}, telegram_id=${telegramId}`);

            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const requestStartedAt = Date.now();
            const res = await fetch(`${API_BASE}/getLiveBetBTN?match=${encodeURIComponent(matchName)}&score=${encodeURIComponent(score)}&elapsed=${encodeURIComponent(elapsed || '')}&date=${encodeURIComponent(date)}&telegram_id=${encodeURIComponent(String(telegramId ?? ''))}`);
            if (!res.ok) {
                throw new Error(await readApiError(res, 'LIVE ставка зараз недоступна для цього матчу.'));
            }
            const data = await res.json();
            if (data?.user_already_had_bet || data?.from_user_history) {
                feedback.cancel();
                silenceGenerationFeedback();
            } else if (firstViewForUser) {
                if (Date.now() - requestStartedAt <= FAST_GENERATION_RESPONSE_MS) {
                    await feedback.finishWithFakeFullRun();
                } else {
                    await feedback.finishWithMinimumDelay();
                }
            }
            dispatch({
                type: actions.LOAD_LIVE_BET,
                payload: {
                    message: data.message,
                    matchStats: data.home_stats && data.away_stats ? {
                        home_stats: data.home_stats,
                        away_stats: data.away_stats
                    } : null,
                    generated_time: data.generated_time || new Date().toISOString(), // ← ДОДАЛИ
                    isLoading: false
                }
            });
            markSeenThisMatchBet('live');
            dispatch({type: 'SET_SUPPRESS_UNTIL', payload: Date.now() + 4000});
            feedback.stop();
            // після успішної генерації — оновимо квоту
            await fetchLiveQuota(true); // миттєво оновить рядок


        } catch (error) {
            console.error('Помилка в liveBetButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        } finally {
            feedback.stop();
        }
    };

    const betButtonClick = async () => {
        if (state.isLoading) {
            console.log('betButtonClick blocked: isLoading is true');
            return;
        }
        const seenPrematchBet = await fetchSeenMatchBetFromHistory('prematch');
        if (seenPrematchBet.found && (seenPrematchBet.data?.message || seenPrematchBet.data?.opinion)) {
            silenceGenerationFeedback();
            setActiveTab(null);
            dispatch({
                type: actions.LOAD_BET,
                payload: {
                    message: seenPrematchBet.data.message || seenPrematchBet.data.opinion,
                    generated_time: seenPrematchBet.data.generated_time || null,
                    isLoading: false,
                },
            });
            window.requestAnimationFrame(() => {
                document.querySelector(`.${styles.infoPanel}`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
            return;
        }
        if (state.prematchBetMessage) {
            setActiveTab(null);
            silenceGenerationFeedback();
            dispatch({
                type: actions.LOAD_BET,
                payload: {
                    message: state.prematchBetMessage,
                    generated_time: state.prematchGeneratedAt || null,
                    isLoading: false,
                },
            });
            markSeenThisMatchBet('prematch');
            window.requestAnimationFrame(() => {
                document.querySelector(`.${styles.infoPanel}`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
            return;
        }
        if (analysisAvailability.checked && analysisAvailability.can_get_bet === false) {
            dispatch({
                type: actions.SET_ERROR,
                payload: {
                    error: analysisAvailability.reason || 'На цей матч зараз недостатньо даних для ставки.',
                    isLoading: false,
                }
            });
            return;
        }
        if (!analysisAvailability.checked || analysisAvailability.loading) {
            dispatch({
                type: actions.SET_ERROR,
                payload: {
                    error: 'Ще перевіряю статистику і лінію матчу. Спробуй натиснути через кілька секунд.',
                    isLoading: false,
                }
            });
            return;
        }
        if (!(await confirmSimpleOddsWarning())) {
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('warning');
            return;
        }
        const firstViewForUser = !seenPrematchBet.localSeen;
        setGenerationMode('prematch');
        const feedback = createDelayedGenerationFeedback(firstViewForUser);
        setActiveTab(null);
        if (!firstViewForUser) {
            silenceGenerationFeedback();
        }
        dispatch({type: actions.LOAD_BET, payload: {isLoading: true}});
        try {
            const telegramId = state.user?.telegram_id || window.Telegram?.WebApp?.initDataUnsafe?.user?.id || localStorage.getItem('telegram_id');
            console.log(`betButtonClick called: match=${matchName}, date=${date}, score=${score}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const requestStartedAt = Date.now();
            const res = await fetch(
                `${API_BASE}/getBetBTN?match=${encodeURIComponent(matchName)}&score=${encodeURIComponent(score)}&date=${encodeURIComponent(date)}&telegram_id=${encodeURIComponent(String(telegramId ?? ''))}`
            );

            if (!res.ok) {
                throw new Error(await readApiError(res, 'Ставка зараз недоступна для цього матчу.'));
            }
            const data = await res.json();
            if (data?.user_already_had_bet || data?.from_user_history) {
                feedback.cancel();
                silenceGenerationFeedback();
            } else if (firstViewForUser) {
                if (Date.now() - requestStartedAt <= FAST_GENERATION_RESPONSE_MS) {
                    await feedback.finishWithFakeFullRun();
                } else {
                    await feedback.finishWithMinimumDelay();
                }
            }
            if (demoActive) {
                markDemoBetUsedLocal();
                setDemoStatus((prev) => ({
                    ...(prev || {}),
                    used: true,
                    used_fixture_id: fixtureId || prev?.allowed_fixture_id || null,
                    used_match_name: matchName,
                }));
            }
            dispatch({
                type: actions.LOAD_BET,
                payload: {message: data.message, isLoading: false}
            });
            markSeenThisMatchBet('prematch');
            if (data.odds_warning) {
                dispatch({
                    type: actions.SET_ERROR,
                    payload: {error: data.odds_warning, isLoading: false}
                });
            }
        } catch (error) {
            console.error('Помилка в betButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        } finally {
            feedback.stop();
        }
    };

    const extendedAnalysisButtonClick = async () => {
        if (state.isLoading) return;
        const alreadySeenExtended = hasSeenThisMatchBet('extended');
        if (alreadySeenExtended && state.extendedAnalysisMessage) {
            silenceGenerationFeedback();
            setActiveTab(null);
            dispatch({
                type: actions.LOAD_OPINION,
                payload: {
                    opinion: state.extendedAnalysisMessage,
                    isLoading: false,
                    extended: true,
                }
            });
            window.requestAnimationFrame(() => {
                document.querySelector(`.${styles.infoPanel}`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
            return;
        }
        if (
            analysisAvailability.checked
            && analysisAvailability.can_get_extended === false
            && !inventoryFeatures.extended_match_analysis_cached
        ) {
            dispatch({
                type: actions.SET_ERROR,
                payload: {
                    error: analysisAvailability.reason || 'Розширений розбір зараз недоступний для цього матчу.',
                    isLoading: false,
                }
            });
            return;
        }
        setActiveTab(null);
        setGenerationMode('prematch');
        const feedback = createDelayedGenerationFeedback(!alreadySeenExtended);
        if (alreadySeenExtended) {
            silenceGenerationFeedback();
        }
        dispatch({type: actions.LOAD_OPINION, payload: {isLoading: true}});
        try {
            const telegramId = state.user?.telegram_id || user?.telegram_id || window.Telegram?.WebApp?.initDataUnsafe?.user?.id || localStorage.getItem('telegram_id');
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const requestStartedAt = Date.now();
            const res = await fetch(
                `${API_BASE}/getExtendedMatchAnalysisBTN?match=${encodeURIComponent(matchName)}&score=${encodeURIComponent(score)}&date=${encodeURIComponent(date)}&telegram_id=${encodeURIComponent(String(telegramId ?? ''))}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'Розширений розбір зараз недоступний для цього матчу.'));
            }
            const data = await res.json();
            if (!alreadySeenExtended) {
                if (Date.now() - requestStartedAt <= FAST_GENERATION_RESPONSE_MS) {
                    await feedback.finishWithFakeFullRun();
                } else {
                    await feedback.finishWithMinimumDelay();
                }
            }
            const extendedMessage = data.message || data.opinion || 'Розбір недоступний';
            dispatch({
                type: actions.LOAD_OPINION,
                payload: {opinion: extendedMessage, isLoading: false, extended: true}
            });
            markSeenThisMatchBet('extended');
            if (data.consumed || data.features) {
                setInventoryFeatures(prev => ({
                    ...prev,
                    extended_match_analysis: !!data.features?.extended_match_analysis,
                    extended_match_analysis_active: !!data.features?.extended_match_analysis_active,
                    extended_match_analysis_cached: !!data.features?.extended_match_analysis_cached,
                }));
                fetchInventoryFeatures();
            }
            window.requestAnimationFrame(() => {
                document.querySelector(`.${styles.infoPanel}`)?.scrollIntoView({behavior: 'smooth', block: 'center'});
            });
        } catch (error) {
            console.error('Помилка в extendedAnalysisButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        } finally {
            feedback.stop();
        }
    };

    const opinionButtonClick = async () => {
        if (state.isLoading) {
            console.log('opinionButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_OPINION, payload: {isLoading: true}});
        try {
            const telegramId = state.user?.telegram_id || window.Telegram?.WebApp?.initDataUnsafe?.user?.id || localStorage.getItem('telegram_id');
            console.log(`opinionButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/getBetBTN?match=${encodeURIComponent(matchName)}&score=${encodeURIComponent(score)}&date=${encodeURIComponent(date)}&telegram_id=${encodeURIComponent(String(telegramId ?? ''))}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'Ставка зараз недоступна для цього матчу.'));
            }
            const data = await res.json();
            await saveBet(matchName, 'bet', data.opinion || 'Невідомо');
            dispatch({
                type: actions.LOAD_OPINION,
                payload: {opinion: data.opinion || 'Невідомо', isLoading: false}
            });
        } catch (error) {
            console.error('Помилка в opinionButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const live_opinionButtonClick = async () => {
        if (state.isLoading) {
            console.log('opinionButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_OPINION, payload: {isLoading: true}});
        try {
            const telegramId = state.user?.telegram_id || window.Telegram?.WebApp?.initDataUnsafe?.user?.id || localStorage.getItem('telegram_id');
            console.log(`opinionButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/getLiveBetBTN?match=${encodeURIComponent(matchName)}&score=${encodeURIComponent(score)}&elapsed=${encodeURIComponent(elapsed || '')}&date=${encodeURIComponent(date)}&telegram_id=${encodeURIComponent(String(telegramId ?? ''))}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'LIVE ставка зараз недоступна для цього матчу.'));
            }
            const data = await res.json();
            await saveBet(matchName, 'bet', data.opinion || 'Невідомо');
            dispatch({
                type: actions.LOAD_OPINION,
                payload: {opinion: data.opinion || 'Невідомо', isLoading: false}
            });
        } catch (error) {
            console.error('Помилка в opinionButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const predictionsButtonClick = async () => {
        if (state.isLoading) {
            console.log('predictionsButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_PREDICTIONS, payload: {isLoading: true}});
        try {
            console.log(`predictionsButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/get_predictions?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'Прогнози для цього матчу зараз недоступні.'));
            }
            const data = await res.json();
            dispatch({
                type: actions.LOAD_PREDICTIONS,
                payload: {predictions: data.predictions || null, isLoading: false}
            });
        } catch (error) {
            console.error('Помилка в predictionsButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const prematchOddsButtonClick = async () => {
        if (state.isLoading) {
            console.log('prematchOddsButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_PREMATCH_ODDS, payload: {isLoading: true}});
        try {
            console.log(`prematchOddsButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/get_prematch_odds?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'Коефіцієнти для цього матчу зараз недоступні.'));
            }
            const data = await res.json();
            console.log('Prematch odds data:', data);
            dispatch({
                type: actions.LOAD_PREMATCH_ODDS,
                payload: {odds: Array.isArray(data.odds) ? data.odds : [], isLoading: false}
            });
        } catch (error) {
            console.error('Помилка в prematchOddsButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const liveOddsButtonClick = async () => {
        if (state.isLoading) {
            console.log('liveOddsButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_LIVE_ODDS, payload: {isLoading: true}});
        try {
            console.log(`liveOddsButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/get_live_odds?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'LIVE коефіцієнти для цього матчу зараз недоступні.'));
            }
            const data = await res.json();
            console.log('Live odds data:', data);
            dispatch({
                type: actions.LOAD_LIVE_ODDS,
                payload: {odds: Array.isArray(data.odds) ? data.odds : [], isLoading: false}
            });
        } catch (error) {
            console.error('Помилка в liveOddsButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const teamStatsButtonClick = async () => {
        if (state.isLoading) {
            console.log('teamStatsButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_TEAM_STATS, payload: {isLoading: true}});
        try {
            console.log(`teamStatsButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/get_team_statistics?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}&last_n=10`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'Статистика команд для цього матчу зараз недоступна.'));
            }
            const data = await res.json();
            dispatch({
                type: actions.LOAD_TEAM_STATS,
                payload: {teamStats: data, isLoading: false}
            });
        } catch (error) {
            console.error('Помилка в teamStatsButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const recentMatchesButtonClick = async () => {
        if (state.isLoading) {
            console.log('recentMatchesButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_RECENT_MATCHES, payload: {isLoading: true}});
        try {
            console.log(`recentMatchesButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/get_recent_matches?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}&last_n=10`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'Останні матчі команд зараз недоступні.'));
            }
            const data = await res.json();
            dispatch({
                type: actions.LOAD_RECENT_MATCHES,
                payload: {recentMatches: data, isLoading: false}
            });
        } catch (error) {
            console.error('Помилка в recentMatchesButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const matchStatsButtonClick = async () => {
        if (state.isLoading) {
            console.log('matchStatsButtonClick blocked: isLoading is true');
            return;
        }
        dispatch({type: actions.LOAD_MATCH_STATS, payload: {isLoading: true}});
        try {
            console.log(`matchStatsButtonClick called: match=${matchName}, date=${date}`);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/get_match_statistics?match=${encodeURIComponent(matchName)}&date=${encodeURIComponent(date)}`
            );
            if (!res.ok) {
                throw new Error(await readApiError(res, 'Live-статистика для цього матчу зараз недоступна.'));
            }
            const data = await res.json();
            console.log('matchStatsButtonClick received:', data);
            dispatch({
                type: actions.LOAD_MATCH_STATS,
                payload: {
                    matchStats: data,
                    backendMessage: 'Live Статистика: ' + JSON.stringify(data),
                    isLoading: false
                }
            });
        } catch (error) {
            console.error('Помилка в matchStatsButtonClick:', error.message);
            dispatch({
                type: actions.SET_ERROR,
                payload: {error: error.message, isLoading: false}
            });
        }
    };

    const showStatsButtonClick = () => {
        if (!state.matchStats) {
            matchStatsButtonClick();
        } else {
            dispatch({type: actions.SHOW_STATS});
        }
    };

    const getUniqueMarkets = (odds) => {
        console.log('getUniqueMarkets called with odds:', odds);
        if (!odds || !Array.isArray(odds) || odds.length === 0) {
            console.log('No valid odds data, returning default markets');
            return ['all'];
        }
        const markets = new Set(['all']);
        odds.forEach((bookmaker) => {
            if (bookmaker && bookmaker.bets && Array.isArray(bookmaker.bets)) {
                bookmaker.bets.forEach((bet) => {
                    if (bet && bet.market && typeof bet.market === 'string') {
                        const marketName = marketMap[bet.market] || bet.market || 'Unknown';
                        markets.add(marketName);
                    }
                });
            }
        });
        const result = Array.from(markets);
        console.log('Unique markets:', result);
        return result.length > 0 ? result : ['all'];
    };

    const prematchMarkets = getUniqueMarkets(state.prematchOdds);
    const liveMarkets = getUniqueMarkets(state.liveOdds);

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Невідома дата';
        try {
            const date = new Date(dateStr);
            return date.toLocaleString('uk-UA', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
            });
        } catch {
            return 'Невідома дата';
        }
    };

    const formatDateTime = (dateStr, timeStr) => {
        if (!dateStr && !timeStr) return '';
        try {
            const d = dateStr ? new Date(dateStr) : null;
            const datePart = d && !isNaN(d.getTime())
                ? d.toLocaleDateString('uk-UA', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                })
                : (dateStr || '');

            const timePart =
                timeStr ||
                (d && !isNaN(d.getTime())
                    ? d.toLocaleTimeString('uk-UA', {
                        hour: '2-digit',
                        minute: '2-digit',
                    })
                    : '');

            return `${datePart} ${timePart}`.trim();
        } catch {
            return `${dateStr || ''} ${timeStr || ''}`.trim();
        }
    };

    const getFilteredBets = (odds, market) => {
        if (!odds || !Array.isArray(odds) || market === 'all') return odds || [];
        return odds
            .map((bookmaker) => ({
                ...bookmaker,
                bets: bookmaker.bets?.filter((bet) => marketMap[bet.market] === market || bet.market === market) || [],
            }))
            .filter((bookmaker) => bookmaker.bets && bookmaker.bets.length > 0);
    };

    const handleMarketClick = useCallback((market) => {
        dispatch({type: actions.SET_SELECTED_MARKET, payload: {selectedMarket: market}});
    }, []);

    const renderPredictions = (predictions) => {
        if (!predictions) return null;
        return (
            <div className={styles.infoCard}>
                <h3>Прогнози</h3>
                <p>
                    <strong>Переможець:</strong> {predictions.winner || 'Невідомо'}
                </p>
                <p>
                    <strong>Порада:</strong> {predictions.advice || 'Немає поради'}
                </p>
                <p>
                    <strong>Ймовірності:</strong>
                </p>
                <ul>
                    <li>Дома: {predictions.percent?.home || '0%'}</li>
                    <li>Нічия: {predictions.percent?.draw || '0%'}</li>
                    <li>В гостях: {predictions.percent?.away || '0%'}</li>
                </ul>
                {predictions.h2h?.length > 0 && (
                    <>
                        <h4>Останні матчі (H2H):</h4>
                        <ul>
                            {predictions.h2h.map((match, idx) => (
                                <li key={idx}>
                                    {match.teams.home.name} {match.goals.home} : {match.goals.away} {match.teams.away.name} (
                                    {match.fixture.date.split('T')[0]})
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>
        );
    };

    const renderFormSquares = (form) => {
        if (!form) return null;
        const formArray = form.split('');
        return (
            <div className={styles.formSquares}>
                {formArray.map((result, index) => {
                    let color = '#ccc';
                    let label = '?';
                    switch (result.toUpperCase()) {
                        case 'W':
                            color = '#4CAF50';
                            label = 'W';
                            break;
                        case 'D':
                            color = '#FF9800';
                            label = 'D';
                            break;
                        case 'L':
                            color = '#F44336';
                            label = 'L';
                            break;
                        default:
                            color = '#ccc';
                            label = result;
                    }
                    return (
                        <div key={index} className={styles.formSquare} style={{backgroundColor: color}}>
                            {label}
                        </div>
                    );
                })}
            </div>
        );
    };

    const renderTeamStats = (teamStats) => {
        if (!teamStats) return null;

        const renderSingleStats = (stats, teamName, logo, teamKey) => {
            const played = stats.fixtures?.played || 0;
            const wins = stats.fixtures?.wins || 0;
            const draws = stats.fixtures?.draws || 0;
            const loses = stats.fixtures?.loses || 0;
            const goalsFor = stats.goals?.for?.total || 0;
            const goalsAgainst = stats.goals?.against?.total || 0;
            const cleanSheets = stats.clean_sheet?.total || 0;
            const failedToScore = stats.failed_to_score?.total || 0;
            const form = stats.form || '';

            const biggestWin = Math.max(stats.biggest?.wins?.home || 0, stats.biggest?.wins?.away || 0) || 0;
            const biggestLose = Math.max(stats.biggest?.loses?.home || 0, stats.biggest?.loses?.away || 0) || 0;
            const winStreak = stats.biggest?.streak?.wins || 0;
            const loseStreak = stats.biggest?.streak?.loses || 0;
            const corners = stats.corners?.total || 0;
            const yellowCards = stats.yellow_cards?.total || 0;
            const redCards = stats.red_cards?.total || 0;
            const isExpanded = !!expandedTeamStats[teamKey];

            return (
                <div className={styles.teamStatsCard}>
                    <div className={styles.teamStatsHero}>
                        <div className={styles.teamHeader}>
                            <img src={logo} alt={teamName} className={styles.teamLogo}/>
                            <div className={styles.teamHeaderText}>
                                <h3>{teamName}</h3>
                                <div className={styles.teamSubLabel}>Останні матчі та форма</div>
                            </div>
                        </div>

                        <div className={styles.formHeroBlock}>
                            <div className={styles.formHeroTitle}>Форма</div>
                            {renderFormSquares(form)}
                        </div>
                    </div>

                    <div className={styles.quickStatsGrid}>
                        <div className={styles.quickStatCard}>
                            <div className={styles.quickStatLabel}>Зіграно</div>
                            <div className={styles.quickStatValue}>{played}</div>
                        </div>
                        <div className={styles.quickStatCard}>
                            <div className={styles.quickStatLabel}>Перемоги</div>
                            <div className={styles.quickStatValue}>{wins}</div>
                        </div>
                        <div className={styles.quickStatCard}>
                            <div className={styles.quickStatLabel}>Забито</div>
                            <div className={styles.quickStatValue}>{goalsFor}</div>
                        </div>
                        <div className={styles.quickStatCard}>
                            <div className={styles.quickStatLabel}>Пропущено</div>
                            <div className={styles.quickStatValue}>{goalsAgainst}</div>
                        </div>
                    </div>

                    <div className={styles.moreStatsButtonWrapper}>
                        <button
                            type="button"
                            className={styles.moreStatsButton}
                            onClick={() => toggleTeamStatsExpanded(teamKey)}
                        >
                            {isExpanded ? 'Менше' : 'Більше'}
                        </button>
                    </div>

                    {isExpanded && (
                        <div className={styles.statsGrid}>
                            <div className={styles.statBlock}>
                                <h4>Баланс матчів</h4>
                                <ul>
                                    <li>Перемоги: {wins}</li>
                                    <li>Нічиї: {draws}</li>
                                    <li>Поразки: {loses}</li>
                                </ul>
                            </div>

                            <div className={styles.statBlock}>
                                <h4>Атака</h4>
                                <ul>
                                    <li>Забито: {goalsFor}</li>
                                    <li>Середньо: {(stats.goals?.for?.average || 0).toFixed(2)}</li>
                                    <li>Не забили: {failedToScore}</li>
                                </ul>
                            </div>

                            <div className={styles.statBlock}>
                                <h4>Захист</h4>
                                <ul>
                                    <li>Пропущено: {goalsAgainst}</li>
                                    <li>Середньо: {(stats.goals?.against?.average || 0).toFixed(2)}</li>
                                    <li>Сухі матчі: {cleanSheets}</li>
                                </ul>
                            </div>

                            <div className={styles.statBlock}>
                                <h4>Серії та рекорди</h4>
                                <ul>
                                    <li>Серія перемог: {winStreak}</li>
                                    <li>Серія поразок: {loseStreak}</li>
                                    <li>Найбільша перемога: {biggestWin}</li>
                                    <li>Найбільша поразка: {biggestLose}</li>
                                </ul>
                            </div>

                            <div className={styles.statBlock}>
                                <h4>Додатково</h4>
                                <ul>
                                    <li>Кутові: {corners}</li>
                                    <li>Жовті картки: {yellowCards}</li>
                                    <li>Червоні картки: {redCards}</li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            );
        };

        return (
            <div className={styles.teamStatsContainer}>
                <h2 className={styles.sectionTitle}>Статистика команд</h2>
                <div className={styles.teamStatsWrapper}>
                    {renderSingleStats(teamStats.home_stats, homeTeam, homeLogo, 'home')}
                    {renderSingleStats(teamStats.away_stats, awayTeam, awayLogo, 'away')}
                </div>
            </div>
        );
    };

    const renderRecentMatches = (payload) => {
        if (!payload) return null;

        const renderPattern = (label, value) => {
            if (!value || !value.text) return null;
            return (
                <div className={styles.recentPatternChip}>
                    <span>{label}</span>
                    <strong>{value.text}</strong>
                </div>
            );
        };

        const renderMatch = (item, index) => {
            const result = String(item.result || '').toUpperCase();
            const resultClass = result === 'W'
                ? styles.recentResultWin
                : result === 'L'
                    ? styles.recentResultLose
                    : styles.recentResultDraw;
            const context = item.context || {};
            const teamStats = item.team_stats || {};
            const matchStats = item.match_stats || {};
            const statValue = (value) => value === null || value === undefined ? '-' : value;
            return (
                <div key={`${item.fixture_id || index}-${item.date || index}`} className={styles.recentMatchCard}>
                    <div className={styles.recentMatchTop}>
                        <span className={`${styles.recentResultBadge} ${resultClass}`}>{result || '-'}</span>
                        <div>
                            <strong>{item.opponent || 'Суперник невідомий'}</strong>
                            <small>{item.date || 'Дата невідома'} · {item.venue === 'home' ? 'вдома' : 'виїзд'}</small>
                        </div>
                        <b>{item.score || '-'}</b>
                    </div>
                    <div className={styles.recentContextLine}>
                        {(context.competition || 'Турнір невідомий')}
                        {context.country ? ` · ${context.country}` : ''}
                        {context.importance ? ` · ${context.importance}` : ''}
                    </div>
                    <div className={styles.recentStatsLine}>
                        {matchStats.stats_available === false ? (
                            <span>Статистика матчу недоступна</span>
                        ) : (
                            <>
                                <span>Удари в ствір: {statValue(teamStats.shots_on_goal)}</span>
                                <span>Удари: {statValue(teamStats.total_shots)}</span>
                                <span>Кутові матчу: {statValue(matchStats.corner_kicks)}</span>
                                <span>ЖК матчу: {statValue(matchStats.yellow_cards)}</span>
                            </>
                        )}
                    </div>
                </div>
            );
        };

        const renderTeamRecent = (title, matches, patterns) => (
            <div className={styles.recentTeamColumn}>
                <div className={styles.recentTeamHeader}>
                    <h3>{title}</h3>
                    <span>{matches?.length || 0} матчів</span>
                </div>
                <div className={styles.recentPatternsRow}>
                    {renderPattern('Забивали', patterns?.scored)}
                    {renderPattern('Пропускали', patterns?.conceded)}
                    {renderPattern('BTTS', patterns?.btts)}
                    {renderPattern('ТБ 2.5', patterns?.over_2_5_goals)}
                </div>
                <div className={styles.recentMatchesList}>
                    {(matches || []).length ? (matches || []).map(renderMatch) : (
                        <div className={styles.infoMessage}>Останні матчі не знайдено</div>
                    )}
                </div>
            </div>
        );

        const context = payload.match_context || {};

        return (
            <div className={styles.recentMatchesContainer}>
                <div className={styles.recentMatchesHeader}>
                    <div>
                        <h2 className={styles.sectionTitle}>Останні матчі</h2>
                        <p>
                            {context.competition || 'Турнір невідомий'}
                            {context.country ? ` · ${context.country}` : ''}
                            {context.importance ? ` · важливість: ${context.importance}` : ''}
                        </p>
                    </div>
                    <span>last {payload.last_n || 10}</span>
                </div>
                <div className={styles.recentTeamsGrid}>
                    {renderTeamRecent(homeTeam, payload.home_recent_matches, payload.home_patterns)}
                    {renderTeamRecent(awayTeam, payload.away_recent_matches, payload.away_patterns)}
                </div>
            </div>
        );
    };

    const renderMarketsCarousel = (odds, markets) => {
        if (!markets || markets.length === 0) {
            console.log('No markets to display');
            return null;
        }

        const scrollLeft = () => {
            const carousel = document.querySelector(`.${styles.carouselScroll}`);
            if (carousel) {
                carousel.scrollBy({left: -150, behavior: 'smooth'});
            }
        };

        const scrollRight = () => {
            const carousel = document.querySelector(`.${styles.carouselScroll}`);
            if (carousel) {
                carousel.scrollBy({left: 150, behavior: 'smooth'});
            }
        };

        return (
            <div className={styles.carouselContainer}>
                <button className={styles.carouselArrow} onClick={scrollLeft}>
                    ←
                </button>
                <div className={styles.carouselScroll}>
                    {markets.map((market, index) => (
                        <button
                            key={`market-${market}-${index}`}
                            onClick={() => handleMarketClick(market)}
                            className={`${styles.marketButton} ${state.selectedMarket === market ? styles.marketButtonActive : ''}`}
                        >
                            {market === 'all' ? 'Всі ринки' : translateMarketName(market)}
                        </button>
                    ))}
                </div>
                <button className={styles.carouselArrow} onClick={scrollRight}>
                    →
                </button>
            </div>
        );
    };

    const renderBets = (odds, title) => {
        if (!odds || odds.length === 0) return <p>{title}: Дані відсутні</p>;

        const filteredOdds = getFilteredBets(odds, state.selectedMarket);
        if (filteredOdds.length === 0) return <p>Немає коефіцієнтів для вибраного ринку</p>;

        const bookmaker = filteredOdds[0];
        if (!bookmaker) return <p>Немає коефіцієнтів для відображення</p>;

        return (
            <div className={styles.oddsBoard}>
                <div className={styles.oddsBookmakerCard}>
                    <div className={styles.oddsBookmakerName}>{bookmaker.name}</div>

                    {bookmaker.bets.map((bet, betIdx) => {
                        const values = Array.isArray(bet.values) ? bet.values : [];
                        return (
                            <div key={betIdx} className={styles.oddsMarketSection}>
                                <div className={styles.oddsMarketTitleRow}>
                                    <div className={styles.oddsMarketTitle}>
                                        {translateMarketName(bet.market)}
                                    </div>
                                </div>

                                <div className={styles.oddsValuesGrid}>
                                    {values.map((value, valIdx) => (
                                        <div key={valIdx} className={styles.oddsValueCard}>
                                            <div className={styles.oddsValueLabel}>
                                                {translateBetValue(value.value, bet.market)}
                                            </div>
                                            <div className={styles.oddsValueOdd}>{value.odd}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderMatchStats = (stats) => {
        if (!stats || !stats.home_stats || !stats.away_stats) {
            return <p>Статистика недоступна</p>;
        }

        // Універсальний парсер значень (числа / "45%" / рядки)
        const parseValue = (value) => {
            if (value === null || value === undefined) return 0;
            if (typeof value === 'number') return value;
            const str = String(value).trim();
            if (str.endsWith('%')) {
                const num = Number(str.slice(0, -1).trim());
                return Number.isNaN(num) ? 0 : num;
            }
            const num = Number(str);
            return Number.isNaN(num) ? 0 : num;
        };

        const statFields = [
            {key: 'shots_on_goal', label: 'Удари в площину'},
            {key: 'shots_off_goal', label: 'Удари повз'},
            {key: 'total_shots', label: 'Усього ударів'},
            {key: 'blocked_shots', label: 'Заблоковано ударів'},
            {key: 'shots_insidebox', label: 'Удари зі штрафного'},
            {key: 'shots_outsidebox', label: 'Удари здалеку'},
            {key: 'fouls', label: 'Порушення'},
            {key: 'corner_kicks', label: 'Кутові'},
            {key: 'offsides', label: 'Поза грою'},
            {key: 'ball_possession', label: 'Володіння м’ячем'},
            {key: 'yellow_cards', label: 'Жовті картки'},
            {key: 'red_cards', label: 'Червоні картки'},
            {key: 'goalkeeper_saves', label: 'Сейви'},
            {key: 'total_passes', label: 'Усього передач'},
            {key: 'passes_accurate', label: 'Точні передачі'},
            {key: 'passes_percentage', label: 'Точність передач'},
        ];

        return (
            <div className={styles.statsContainer}>
                <h3>Live Статистика</h3>
                {statFields.map((field) => {
                    const rawHome = stats.home_stats[field.key];
                    const rawAway = stats.away_stats[field.key];

                    const isPercentage =
                        field.key.includes('possession') || field.key.includes('percentage');

                    const homeVal = parseValue(rawHome);
                    const awayVal = parseValue(rawAway);

                    const homeDisplay = isPercentage ? `${homeVal}%` : homeVal;
                    const awayDisplay = isPercentage ? `${awayVal}%` : awayVal;

                    let homeWidth = 50;
                    let awayWidth = 50;

                    if (isPercentage) {
                        // Відсотки напряму, але обмежуємо 5–100% щоб не було повністю порожніх/забитих смуг
                        homeWidth = Math.max(5, Math.min(homeVal, 100));
                        awayWidth = Math.max(5, Math.min(awayVal, 100));
                    } else {
                        // Абсолютні значення — нормалізуємо до total, щоб смуги завжди були пропорційні
                        const total = homeVal + awayVal;
                        if (total > 0) {
                            homeWidth = Math.max(5, (homeVal / total) * 100);
                            awayWidth = Math.max(5, (awayVal / total) * 100);
                        }
                    }

                    return (
                        <div key={field.key} className={styles.statRow}>
              <span className={styles.statValue}>
                {homeDisplay}
              </span>
                            <div className={styles.statBarContainer}>
                                <div
                                    className={styles.statBarHome}
                                    style={{width: `${homeWidth}%`}}
                                />
                                <span className={styles.statLabel}>{field.label}</span>
                                <div
                                    className={styles.statBarAway}
                                    style={{width: `${awayWidth}%`}}
                                />
                            </div>
                            <span className={styles.statValue}>
                {awayDisplay}
              </span>
                        </div>
                    );
                })}
            </div>
        );
    };

    const dateTimeLabel = formatDateTime(date, time);

    const statusLabel = (() => {
        if (isMatchFinished) return 'Матч завершено';
        if (status === 'HT') return 'Перерва';
        if (isLiveStarted) {
            if (elapsed) return `LIVE — ${elapsed}′ хвилина`;
            return 'LIVE';
        }
        if (isNotStarted) return 'Ще не розпочався';
        return status || '';
    })();

    useEffect(() => {
        if (!demoActive || !date) return;
        const telegramId = user?.telegram_id || window.Telegram?.WebApp?.initDataUnsafe?.user?.id || localStorage.getItem('telegram_id');
        if (!telegramId) return;

        let cancelled = false;
        const loadDemoStatus = async () => {
            try {
                const API_BASE = process.env.REACT_APP_API_BASE || '/api';
                const qs = new URLSearchParams({telegram_id: String(telegramId), date});
                const res = await fetch(`${API_BASE}/demo-access/status?${qs.toString()}&_=${Date.now()}`, {
                    headers: {'Cache-Control': 'no-cache'},
                });
                if (!res.ok) throw new Error(`HTTP ${res.status}`);
                const data = await res.json();
                if (!cancelled) setDemoStatus(data);
            } catch (error) {
                console.error('Demo status error:', error.message);
                if (!cancelled) setDemoStatus({demo: true, used: true, allowed_fixture_id: null});
            }
        };

        loadDemoStatus();
        return () => {
            cancelled = true;
        };
    }, [demoActive, date, user?.telegram_id]);

    const demoUsed = demoActive && (!!demoStatus?.used || isDemoBetUsedLocal());
    const demoAllowedMatch = !demoActive || (
        !demoUsed &&
        (
            (fixtureId && demoStatus?.allowed_fixture_id && String(fixtureId) === String(demoStatus.allowed_fixture_id)) ||
            demoStatus?.allowed_match?.match_name === matchName
        )
    );
    const demoLocked = demoActive && (!demoAllowedMatch || demoUsed);

// ---- CTA logic for main action button ----
    let ctaText = '';
    let ctaOnClick = null;
    let ctaDisabled = false;
    let ctaHelper = '';
    const liveReadinessReasonText = {
        match_not_found: 'Матч не знайдено в базі',
        match_finished: 'Матч завершено',
        not_live: 'Матч ще не в live',
        outside_live_window: 'LIVE-ставки доступні приблизно з 25′ до 75′ хв.',
        no_live_stats: 'Немає live-статистики — ставки не приймаються',
        no_domination: 'Потрібна сильніша перевага однієї з команд за статистикою',
        no_playable_live_odds: 'У live-лінії поки немає нормального ринку для ставки',
        late_needs_high_domination: 'Після 70′ потрібна дуже сильна перевага',
        ready: liveQuotaMsg || 'LIVE-ставки з урахуванням квоти',
    };

    if (isMatchFinished) {
        ctaText = 'Матч завершено';
        ctaDisabled = true;
        ctaHelper = 'Ставки недоступні';
    } else if (demoLocked) {
        ctaText = demoUsed ? 'Демо-ставку використано' : 'Матч під замком';
        ctaDisabled = true;
        ctaHelper = demoUsed
            ? 'Для повного доступу вступи в Сім’ю.'
            : 'У демо відкритий тільки один матч дня.';
    } else if (demoActive && isLiveStarted) {
        ctaText = 'LIVE під замком';
        ctaDisabled = true;
        ctaHelper = 'У демо доступна тільки одна prematch-ставка.';
    } else if (isLiveStarted) {
        if (!canLiveByTime) {
            ctaText = 'LIVE-ставка недоступна';
            ctaDisabled = true;
            ctaHelper = 'LIVE-ставки доступні приблизно з 25′ до 75′ хв.';
        } else if (!hasStatsForLive) {
            ctaText = 'Статистика недоступна';
            ctaDisabled = true;
            ctaHelper = 'Немає live-статистики — ставки не приймаються';
        } else {
            ctaText = isBetGenerating ? 'Обробка...' : 'Отримати LIVE ставку';
            ctaOnClick = liveBetButtonClick;
            ctaDisabled = state.isLoading || !liveWatchStatus.readiness_checked || !liveWatchStatus.is_ready;
            ctaHelper = !liveWatchStatus.readiness_checked
                ? 'Перевіряю live-статистику і лінію ставок...'
                : (liveReadinessReasonText[liveWatchStatus.reason] || 'LIVE ставка поки недоступна');
        }
    } else {
        if (analysisAvailability.loading) {
            ctaText = 'Перевіряю дані';
            ctaDisabled = true;
            ctaHelper = 'Перевіряю лінію коефіцієнтів і recent-матчі команд...';
        } else if (!analysisAvailability.checked) {
            ctaText = 'Перевіряю дані';
            ctaDisabled = true;
            ctaHelper = 'Дочекайся перевірки даних матчу перед генерацією.';
        } else if (analysisAvailability.checked && analysisAvailability.can_get_bet === false) {
            ctaText = 'Ставка недоступна';
            ctaDisabled = true;
            ctaHelper = analysisAvailability.reason || 'На цей матч зараз недостатньо даних для ставки.';
        } else {
            ctaText = isBetGenerating ? 'Обробка...' : 'Отримати ставку';
            ctaOnClick = betButtonClick;
            ctaDisabled = state.isLoading;
            ctaHelper = analysisAvailability.simple_warning || 'Прематч-ставка до початку матчу';
        }
    }

    const liveWatchButtonText = liveWatchLoading
        ? 'Оновлюю сповіщення...'
        : liveWatchStatus.is_watching
            ? (liveWatchStatus.is_ready ? 'LIVE ставка вже доступна' : 'Стежу за LIVE ставкою')
            : 'Сповістити, коли LIVE ставка буде доступна';

    const liveWatchHelper = liveWatchStatus.is_watching
        ? (liveWatchStatus.is_ready
            ? 'Умови зійшлись, можна натискати основну LIVE-кнопку.'
            : 'Бот напише в Telegram, коли основна LIVE-кнопка стане активною.')
        : 'Одне повідомлення в Telegram для цього матчу.';

    const visibleBackendMessage = state.backendMessage && !String(state.backendMessage).startsWith('Live Статистика:')
        ? state.backendMessage
        : '';
    const hasPrematchBet = !!String(state.prematchBetMessage || '').trim();
    const hasLiveBet = !!String(state.liveBetMessage || '').trim();
    const activeBetType = state.activeBetType || (hasLiveBet ? 'live' : hasPrematchBet ? 'prematch' : null);
    const showPrematchRecallButton = isLiveStarted && !isMatchFinished && hasPrematchBet;

// ---- Tabs config ----
    const tabs = [
        {id: 'opinion', label: 'Думка'},
        {id: 'teams', label: 'Форма'},
        {id: 'matches', label: 'Матчі'},
        {id: 'odds', label: 'Коефіцієнти'},
        ...(isLiveStarted && !isMatchFinished
            ? [{id: 'stats', label: 'Live Статистика'}]
            : []),
    ];

    useEffect(() => {
        if (activeTab === 'stats' && (!isLiveStarted || isMatchFinished)) {
            setActiveTab('odds');
        }
    }, [activeTab, isLiveStarted, isMatchFinished]);

    const handleTabSelect = async (tabId) => {
        if (demoActive) {
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('warning');
            return;
        }
        setActiveTab(tabId);
        switch (tabId) {
            case 'opinion':
                if (isMatchFinished) return;
                if (isLiveStarted && canLiveByTime && hasStatsForLive) {
                    await live_opinionButtonClick();
                } else {
                    await opinionButtonClick();
                }
                break;
            case 'teams':
                await teamStatsButtonClick();
                break;
            case 'matches':
                await recentMatchesButtonClick();
                break;
            case 'odds':
                await prematchOddsButtonClick();
                break;
            case 'stats':
                if (isLiveStarted && !isMatchFinished) {
                    await matchStatsButtonClick();
                }
                break;
            default:
                break;
        }
    };

    const toggleTeamStatsExpanded = (teamKey) => {
        setExpandedTeamStats((prev) => ({
            ...prev,
            [teamKey]: !prev[teamKey],
        }));
    };

    const teamNameByKey = {
        home: homeTeam,
        away: awayTeam,
    };

    const loadFavoriteTeams = useCallback(async () => {
        const telegramId = user?.telegram_id;
        if (demoActive) return;
        if (!telegramId) return;

        try {
            setFavoriteTeamsLoading(true);
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(
                `${API_BASE}/user/${telegramId}/favorite-teams?_=${Date.now()}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                    },
                    cache: 'no-store',
                }
            );

            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }

            const data = await res.json();
            const favorites = Array.isArray(data.favorite_teams) ? data.favorite_teams : [];

            setFavoriteTeams({
                home: favorites.includes(homeTeam),
                away: favorites.includes(awayTeam),
            });
        } catch (error) {
            console.error('Помилка завантаження обраних команд:', error.message);
        } finally {
            setFavoriteTeamsLoading(false);
        }
    }, [user?.telegram_id, homeTeam, awayTeam, demoActive]);

    const toggleFavoriteTeam = async (teamKey) => {
        const telegramId = user?.telegram_id;
        const teamName = teamNameByKey[teamKey];

        if (!telegramId || !teamName || favoriteRequestRef.current) {
            return;
        }

        const prevValue = !!favoriteTeams[teamKey];

        setFavoriteTeams((prev) => ({
            ...prev,
            [teamKey]: !prev[teamKey],
        }));

        favoriteRequestRef.current = true;
        try {
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(`${API_BASE}/user/favorite-teams/toggle`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                },
                body: JSON.stringify({
                    telegram_id: telegramId,
                    team_name: teamName,
                }),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }

            const data = await res.json();
            const isFavorite = !!data.is_favorite;

            setFavoriteTeams((prev) => ({
                ...prev,
                [teamKey]: isFavorite,
            }));
            await loadMatchLiveWatchStatus();
        } catch (error) {
            console.error('Помилка оновлення обраної команди:', error.message);
            setFavoriteTeams((prev) => ({
                ...prev,
                [teamKey]: prevValue,
            }));
        } finally {
            favoriteRequestRef.current = false;
        }
    };

    const getTelegramId = useCallback(() => (
        user?.telegram_id ||
        window.Telegram?.WebApp?.initDataUnsafe?.user?.id ||
        localStorage.getItem('telegram_id')
    ), [user?.telegram_id]);

    const loadMatchLiveWatchStatus = useCallback(async () => {
        if (demoActive) return;
        const telegramId = getTelegramId();
        if (!telegramId || !matchName || !date) return;

        try {
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const qs = new URLSearchParams({match: matchName, date}).toString();
            const res = await fetch(
                `${API_BASE}/user/${telegramId}/match-live-watch/status?${qs}&_=${Date.now()}`,
                {
                    headers: {
                        'Cache-Control': 'no-cache, no-store, must-revalidate',
                        'Pragma': 'no-cache',
                    },
                    cache: 'no-store',
                }
            );

            if (!res.ok) return;

            const data = await res.json();
            setLiveWatchStatus({
                is_watching: !!data.is_watching,
                is_ready: !!data.is_ready,
                notified_live_ready: !!data.notified_live_ready,
                reason: data.reason || '',
                readiness_checked: true,
            });
        } catch (error) {
            console.error('Помилка завантаження live-спостереження:', error.message);
        }
    }, [getTelegramId, matchName, date, demoActive]);

    const toggleMatchLiveWatch = async () => {
        const telegramId = getTelegramId();

        if (!telegramId) {
            setLiveWatchMessage('Не вдалося визначити Telegram ID');
            window.setTimeout(() => setLiveWatchMessage(''), 2400);
            return;
        }

        if (isMatchFinished || liveWatchRequestRef.current) {
            return;
        }

        liveWatchRequestRef.current = true;
        setLiveWatchLoading(true);
        try {
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const res = await fetch(`${API_BASE}/user/match-live-watch/toggle`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    'Pragma': 'no-cache',
                },
                body: JSON.stringify({
                    telegram_id: Number(telegramId),
                    match: matchName,
                    date,
                }),
            });

            if (!res.ok) {
                const errorText = await res.text();
                throw new Error(`HTTP ${res.status}: ${errorText}`);
            }

            const data = await res.json();
            setLiveWatchStatus({
                is_watching: !!data.is_watching,
                is_ready: !!data.is_ready,
                notified_live_ready: !!data.notified_live_ready,
                reason: data.reason || '',
                readiness_checked: true,
            });
            setLiveWatchMessage(data.is_watching ? 'Сповіщу, коли LIVE ставка стане доступною' : 'Сповіщення вимкнено');
            window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
            window.setTimeout(() => setLiveWatchMessage(''), 2400);
        } catch (error) {
            console.error('Помилка оновлення live-спостереження:', error.message);
            setLiveWatchMessage('Не вдалося оновити сповіщення');
            window.setTimeout(() => setLiveWatchMessage(''), 2400);
        } finally {
            setLiveWatchLoading(false);
            liveWatchRequestRef.current = false;
        }
    };

    useEffect(() => {
        loadFavoriteTeams();
    }, [loadFavoriteTeams]);

    useEffect(() => {
        loadMatchLiveWatchStatus();
    }, [loadMatchLiveWatchStatus]);

    useEffect(() => {
        if (!isLiveStarted || isMatchFinished) return;
        loadMatchLiveWatchStatus();
    }, [state.matchStats, elapsed, isLiveStarted, isMatchFinished, loadMatchLiveWatchStatus]);

    const shareMatch = async () => {
        const betText = String(state.betMessage || '').trim();

        if (!betText) {
            setShareStatus('Спочатку отримай ставку');
            window.setTimeout(() => setShareStatus(''), 2200);
            return;
        }

        try {
            const tg = window.Telegram?.WebApp;
            if (!tg?.initData || typeof tg.shareMessage !== 'function') {
                setShareStatus('Онови Telegram');
                window.setTimeout(() => setShareStatus(''), 2200);
                return;
            }

            setShareStatus('Готую…');
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const response = await fetch(`${API_BASE}/share-bet`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Telegram-Init-Data': tg.initData,
                },
                body: JSON.stringify({
                    home_team: homeTeam,
                    away_team: awayTeam,
                    date_time: dateTimeLabel,
                    bet_text: betText,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${await response.text()}`);
            }

            const data = await response.json();
            if (!data.prepared_message_id) {
                throw new Error('Prepared message id is missing');
            }

            setShareStatus('Обери отримувача');
            tg.shareMessage(data.prepared_message_id, (sent) => {
                setShareStatus(sent ? 'Надіслано' : 'Не надіслано');
                window.setTimeout(() => setShareStatus(''), 2200);
            });
        } catch (error) {
            console.error('Telegram share error:', error);
            setShareStatus('Не вдалося');
            window.setTimeout(() => setShareStatus(''), 2200);
        }
    };

    const renderPremiumList = (items, emptyText) => {
        const list = Array.isArray(items) ? items.filter(Boolean) : [];
        if (!list.length) {
            return <p className={styles.premiumMuted}>{emptyText}</p>;
        }
        return (
            <ul className={styles.premiumList}>
                {list.map((item, index) => (
                    <li key={`${item.slice(0, 24)}-${index}`}>{item}</li>
                ))}
            </ul>
        );
    };

    return (
        <div className={styles.pageContainer}>
            <div className={styles.matchPageHeader}>
                <div className={styles.backButtonContainer}>
                    <button onClick={() => navigate(-1)} className={styles.backButton}>
                        ← Назад
                    </button>
                </div>
                <div className={styles.pageTitle}>
                    <span>GRACH</span>
                    <strong>MATCH CENTER</strong>
                </div>
            </div>


            <div className={styles.matchTitle}>
                {/* Верхній рядок з датою та часом */}
                <div className={styles.matchHeaderTopRow}>
                    <span className={styles.matchDateTime}>
                        {dateTimeLabel}
                    </span>
                    <button
                        type="button"
                        className={`${styles.shareButton} ${demoActive ? styles.lockedSmallButton : ''}`}
                        onClick={demoActive ? undefined : shareMatch}
                        disabled={demoActive}
                    >
                        <span aria-hidden="true">{demoActive ? '🔒' : '↗'}</span>
                        {shareStatus || (demoActive ? 'Закрито' : 'Надіслати ставку')}
                    </button>
                </div>

                {/* Основний рядок: команда – рахунок – команда */}
                <div className={styles.matchHeaderMainRow}>
                    {/* Ліва команда */}
                    <div className={styles.teamSide}>
                        <button
                            type="button"
                            className={`${styles.starButton} ${favoriteTeams.home ? styles.starButtonActive : ''}`}
                            onClick={() => toggleFavoriteTeam('home')}
                            aria-label={favoriteTeams.home ? 'Прибрати з обраного' : 'Додати в обране'}
                            disabled={favoriteTeamsLoading || demoActive}
                        >
                            {demoActive ? '🔒' : (favoriteTeams.home ? '★' : '☆')}
                        </button>
                        <div className={styles.teamBadgeWrapper}>
                            <img
                                src={homeLogo}
                                alt={homeTeam}
                                className={styles.teamLogoBig}
                            />
                        </div>
                        <div className={styles.teamNameLarge}>
                            {homeTeam}
                        </div>
                        {renderCompactForm(state.teamStats?.home_stats?.form)}
                    </div>

                    {/* Центр: рахунок + статус */}
                    <div className={styles.centerColumn}>
                        <div className={`${styles.matchScore} ${isNotStarted ? styles.kickoffScore : ''} ${isLiveStarted ? styles.liveScore : ''}`}>
                            {isNotStarted
                                ? (time || '—:—')
                                : (score || '0 : 0')}
                        </div>
                        <div className={`${styles.matchPhase} ${isLiveStarted ? styles.matchPhaseLive : ''}`}>
                            {statusLabel}
                        </div>
                    </div>

                    {/* Права команда */}
                    <div className={styles.teamSide}>
                        <button
                            type="button"
                            className={`${styles.starButton} ${favoriteTeams.away ? styles.starButtonActive : ''}`}
                            onClick={() => toggleFavoriteTeam('away')}
                            aria-label={favoriteTeams.away ? 'Прибрати з обраного' : 'Додати в обране'}
                            disabled={favoriteTeamsLoading || demoActive}
                        >
                            {demoActive ? '🔒' : (favoriteTeams.away ? '★' : '☆')}
                        </button>
                        <div className={styles.teamBadgeWrapper}>
                            <img
                                src={awayLogo}
                                alt={awayTeam}
                                className={styles.teamLogoBig}
                            />
                        </div>
                        <div className={styles.teamNameLarge}>
                            {awayTeam}
                        </div>
                        {renderCompactForm(state.teamStats?.away_stats?.form)}
                    </div>
                </div>
            </div>

            <div className={styles.buttonGroup}>
                <div className={styles.mainActionWrapper}>
                    {!isMatchFinished ? (
                        <>
                            {isLiveStarted && canLiveByTime && hasStatsForLive && liveQuotaMsg && (
                                <div className={styles.liveQuotaLabel}>
                                    <strong>Ліміт:</strong> {liveQuotaMsg}
                                </div>
                            )}

                            <button
                                className={`${styles.primaryActionButton} 
                                ${isLiveStarted && canLiveByTime && hasStatsForLive ? styles.liveButton : ''} 
                                ${ctaDisabled ? styles.disabledButton : ''} 
                                ${state.apiMessage ? styles.stopAnimation : ''} 
                                ${isBetGenerating ? styles.generatingButton : ''}
                                ${isBetGenerating && generationMode === 'live' ? styles.liveGeneratingButton : ''}
                                ${generationCompleted ? styles.generationComplete : ''}
                                ${isLiveStarted && canLiveByTime && hasStatsForLive && !liveWatchStatus.is_ready ? styles.disabledLiveButton : ''}`}
                                disabled={ctaDisabled}
                                onClick={ctaOnClick || undefined}
                            >
                                {isBetGenerating ? (
                                    <>
                                        <span className={styles.generationContent}>
                                            <span className={styles.generationTopRow}>
                                                <span className={styles.generationLabel}>
                                                    <span className={styles.generationDot}/>
                                                    {generationMode === 'live' ? 'LIVE аналіз' : 'Аналіз матчу'}
                                                </span>
                                                <strong className={styles.generationPercent}>{generationProgressLabel}%</strong>
                                            </span>
                                            <span className={styles.generationTrack} aria-hidden="true">
                                                <span
                                                    className={styles.generationTrackFill}
                                                    style={generationProgressStyle}
                                                />
                                            </span>
                                        </span>
                                    </>
                                ) : (
                                    <span className={styles.actionButtonLabel}>
                                        {generationCompleted ? 'Ставка готова' : ctaText}
                                    </span>
                                )}
                            </button>

                            {ctaHelper && (
                                <div className={styles.ctaHelperText}>{ctaHelper}</div>
                            )}

                            {showPrematchRecallButton && (
                                <div className={styles.betTypeSwitch}>
                                    <button
                                        type="button"
                                        className={activeBetType === 'prematch' ? styles.betTypeSwitchActive : ''}
                                        onClick={() => dispatch({
                                            type: actions.SHOW_BET_TYPE,
                                            payload: {betType: 'prematch'},
                                        })}
                                    >
                                        PREMATCH
                                    </button>
                                </div>
                            )}

                            {demoActive && (
                                <div className={styles.demoMatchNotice}>
                                    Демо відкриває тільки одну ставку. Думка, форма, коефіцієнти і LIVE-функції доступні після вступу в Сім’ю.
                                </div>
                            )}

	                            {!demoActive && (
	                                <>
	                                    {inventoryFeatures.extended_match_analysis
                                            && !analysisAvailability.loading
                                            && (analysisAvailability.can_get_extended || inventoryFeatures.extended_match_analysis_cached)
                                            && (!isMatchFinished || inventoryFeatures.extended_match_analysis_cached) && (
	                                        <button
	                                            type="button"
	                                            className={styles.extendedAnalysisButton}
	                                            onClick={extendedAnalysisButtonClick}
	                                            disabled={state.isLoading}
	                                        >
	                                            <span aria-hidden="true">AI</span>
	                                            Розширений розбір
	                                        </button>
	                                    )}

	                                    <button
	                                        type="button"
	                                        className={`${styles.liveWatchButton} ${liveWatchStatus.is_watching ? styles.liveWatchButtonActive : ''} ${liveWatchStatus.is_ready ? styles.liveWatchButtonReady : ''}`}
                                        onClick={toggleMatchLiveWatch}
                                        disabled={liveWatchLoading}
                                        aria-pressed={liveWatchStatus.is_watching}
                                    >
                                        <span aria-hidden="true">{liveWatchStatus.is_watching ? '🔔' : '🔕'}</span>
                                        {liveWatchButtonText}
                                    </button>

                                    <div className={styles.liveWatchHelperText}>
                                        {liveWatchMessage || liveWatchHelper}
                                    </div>
                                </>
                            )}

                            <div className={styles.tabsContainer}>
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        className={`${styles.tabButton} ${activeTab === tab.id && !demoActive ? styles.tabButtonActive : ''} ${demoActive ? styles.tabButtonLocked : ''}`}
                                        onClick={() => handleTabSelect(tab.id)}
                                        disabled={demoActive}
                                    >
                                        {demoActive ? '🔒 ' : ''}{tab.label}
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className={styles.infoMessage}>
                            <strong>Матч завершено.</strong> Ставки недоступні.
                        </div>
                    )}
                </div>


            </div>

            {visibleBackendMessage && (
                <div className={styles.infoMessage}>
                    <strong>Важливо.</strong> {visibleBackendMessage}
                </div>
            )}

            {state.apiMessage && (() => {
                const premiumSections = parsePremiumValueScan(state.apiMessage);
                const infoSections = premiumSections ? null : parseInfoSections(state.apiMessage);
                if (!premiumSections && !infoSections) return null;

                return (
                    <div className={`${styles.infoPanel} ${premiumSections ? styles.premiumInfoPanel : ''} ${activeBetType === 'live' ? styles.liveInfoPanel : ''}`}>
                        <div className={styles.infoPanelAccent}/>
                        <div className={styles.infoPanelInner}>
                            <div className={styles.infoPanelHeader}>
                                <div className={styles.infoPanelIcon}>{premiumSections ? 'V' : (activeBetType === 'live' ? 'L' : 'G')}</div>
                                <div>
                                    <div className={styles.infoPanelKicker}>
                                        {premiumSections ? 'Premium Value Scanner' : (activeBetType === 'live' ? 'Live Grach Intelligence' : 'Grach Intelligence')}
                                    </div>
                                    <div className={styles.infoPanelTitle}>
                                        {premiumSections
                                            ? 'VALUE-РОЗБІР'
                                            : (activeBetType === 'live' ? 'LIVE СТАВКА' : (infoSections.mode === 'opinion' ? 'ДУМКА ПО МАТЧУ' : 'АНАЛІЗ МАТЧУ'))}
                                    </div>
                                </div>
                                <div className={premiumSections ? styles.premiumStatus : styles.analysisStatus}>
                                    {premiumSections ? 'PREMIUM' : (activeBetType === 'live' ? 'LIVE' : 'ГОТОВО')}
                                </div>
                            </div>

                            {premiumSections && (
                                <>
                                    {premiumSections.noBet ? (
                                        <div className={styles.premiumNoBet}>
                                            <strong>NO BET</strong>
                                            <p>{premiumSections.final || 'У цьому матчі немає достатньо сильної value-ставки. Предмет не списано.'}</p>
                                        </div>
                                    ) : (
                                        <>
                                            <div className={styles.premiumHero}>
                                                <div className={styles.premiumMainBet}>
                                                    <span>Основна value-ставка</span>
                                                    <strong>{premiumSections.mainBet || 'Ставка не визначена'}</strong>
                                                </div>
                                                <div className={styles.premiumProbability}>
                                                    <span>Оцінка бота</span>
                                                    <strong>{premiumSections.probability || '—'}</strong>
                                                    <div className={styles.premiumProbabilityTrack}>
                                                        <i style={{width: `${premiumSections.probabilityNumber || 0}%`}}/>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className={styles.premiumMetricsGrid}>
                                                <div>
                                                    <span>Кеф лінії</span>
                                                    <strong>{premiumSections.lineOdds || '—'}</strong>
                                                </div>
                                                <div>
                                                    <span>Чесний кеф</span>
                                                    <strong>{premiumSections.fairOdds || '—'}</strong>
                                                </div>
                                                <div>
                                                    <span>Value</span>
                                                    <strong>{premiumSections.value || '—'}</strong>
                                                </div>
                                                <div>
                                                    <span>Рівень</span>
                                                    <strong>{premiumSections.confidence || '—'}</strong>
                                                </div>
                                            </div>
                                        </>
                                    )}

                                    {(premiumSections.matchProfile || premiumSections.scenario) && (
                                        <div className={styles.premiumScenarioStack}>
                                            {premiumSections.matchProfile && (
                                                <div className={`${styles.premiumScenario} ${styles.premiumProfileCard}`}>
                                                    <span>Матч-профіль</span>
                                                    <p>{premiumSections.matchProfile}</p>
                                                </div>
                                            )}
                                            {premiumSections.scenario && (
                                                <div className={styles.premiumScenario}>
                                                    <span>Сценарій матчу</span>
                                                    <p>{premiumSections.scenario}</p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {!!premiumSections.marketRadar?.length && (
                                        <div className={styles.premiumSection}>
                                            <div className={styles.premiumSectionTitle}>Радар ринків</div>
                                            <div className={styles.premiumRadarGrid}>
                                                {premiumSections.marketRadar.slice(0, 4).map((item, index) => {
                                                    const [title, ...rest] = String(item).split('—');
                                                    return (
                                                        <div key={`${item.slice(0, 20)}-${index}`} className={styles.premiumRadarCard}>
                                                            <span>{title?.trim() || `Напрямок ${index + 1}`}</span>
                                                            <p>{rest.join('—').trim() || item}</p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {!!premiumSections.valueMap?.length && (
                                        <div className={styles.premiumSection}>
                                            <div className={styles.premiumSectionTitle}>Value-карта</div>
                                            <div className={styles.premiumValueMap}>
                                                {premiumSections.valueMap.slice(0, 4).map((item, index) => (
                                                    <div key={`${item.slice(0, 24)}-${index}`} className={styles.premiumValueRow}>
                                                        <b>{String(index + 1).padStart(2, '0')}</b>
                                                        <span>{item}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {!!premiumSections.alternatives?.length && (
                                        <div className={styles.premiumSection}>
                                            <div className={styles.premiumSectionTitle}>Запасні входи</div>
                                            {renderPremiumList(premiumSections.alternatives, 'Сильних альтернатив немає.')}
                                        </div>
                                    )}

                                    <div className={styles.premiumGrid}>
                                        <div className={styles.premiumSection}>
                                            <div className={styles.premiumSectionTitle}>Факти</div>
                                            {renderPremiumList(premiumSections.facts, 'Фактів недостатньо для сильної переваги.')}
                                        </div>
                                        <div className={styles.premiumSection}>
                                            <div className={styles.premiumSectionTitle}>Що може зламати</div>
                                            {renderPremiumList(premiumSections.risks, 'Критичних ризиків не виділено.')}
                                        </div>
                                    </div>

                                    {!!premiumSections.checklist?.length && (
                                        <div className={styles.premiumSection}>
                                            <div className={styles.premiumSectionTitle}>Перед матчем перевірити</div>
                                            {renderPremiumList(premiumSections.checklist, 'Додаткових умов для перевірки немає.')}
                                        </div>
                                    )}

                                    <div className={styles.premiumSection}>
                                        <div className={styles.premiumSectionTitle}>Live-план</div>
                                        {renderPremiumList(premiumSections.livePlan, 'Live-план залежить від темпу перших хвилин.')}
                                    </div>

                                    {premiumSections.final && !premiumSections.noBet && (
                                        <div className={styles.premiumFinal}>{premiumSections.final}</div>
                                    )}
                                </>
                            )}

                            {!premiumSections && infoSections.mode === 'opinion' && (
                                <div className={styles.opinionReadingCard}>
                                    <div className={styles.opinionReadingHeader}>
                                        <span>01</span>
                                        <strong>Короткий розбір</strong>
                                    </div>
                                    <div className={styles.opinionParagraphList}>
                                        {(infoSections.opinionParagraphs?.length ? infoSections.opinionParagraphs : [infoSections.raw]).map((paragraph, index) => (
                                            <p key={`${paragraph.slice(0, 24)}-${index}`}>{paragraph}</p>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {!premiumSections && infoSections.mode !== 'opinion' && infoSections.mainBet && (
                                <div className={`${styles.infoSectionBlock} ${styles.infoSectionPrimary}`}>
                                    <div className={styles.infoSectionTitle}>
                                        <span>01</span> Основна ставка
                                    </div>
                                    <div className={styles.infoSectionText}>{infoSections.mainBet}</div>
                                </div>
                            )}

                            {!premiumSections && infoSections.mode !== 'opinion' && infoSections.alternative && (
                                <div className={`${styles.infoSectionBlock} ${styles.infoSectionAlternative}`}>
                                    <div className={styles.infoSectionTitle}>
                                        <span>02</span> Альтернатива
                                    </div>
                                    <div className={styles.infoSectionText}>{infoSections.alternative}</div>
                                </div>
                            )}

                            {!premiumSections && infoSections.mode !== 'opinion' && infoSections.motivation && (
                                <div className={`${styles.infoSectionBlock} ${styles.infoSectionMotivation}`}>
                                    <div className={styles.infoSectionTitle}>
                                        <span>03</span> Мотивація
                                    </div>
                                    <div className={styles.infoSectionText}>{infoSections.motivation}</div>
                                </div>
                            )}

                            {!premiumSections && infoSections.mode !== 'opinion' && infoSections.why && (
                                <div className={`${styles.infoSectionBlock} ${styles.infoSectionWhy}`}>
                                    <div className={styles.infoSectionTitle}>
                                        <span>{infoSections.motivation ? '04' : '03'}</span> Чому саме так
                                    </div>
                                    <div className={styles.infoSectionText}>{infoSections.why}</div>
                                </div>
                            )}

                            {!premiumSections && infoSections.mode !== 'opinion' && infoSections.confidence && (
                                <div className={`${styles.infoSectionBlock} ${styles.infoSectionConfidence}`}>
                                    <div className={styles.infoSectionTitle}>
                                        <span>{infoSections.motivation ? '05' : '04'}</span> Надійність
                                    </div>
                                    <div className={styles.infoSectionText}>{infoSections.confidence}</div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            })()}

            {state.showPredictions && state.predictions && renderPredictions(state.predictions)}
            {state.showTeamStats && state.teamStats && renderTeamStats(state.teamStats)}
            {state.showRecentMatches && state.recentMatches && renderRecentMatches(state.recentMatches)}
            {state.showPrematchOdds && state.prematchOdds && renderMarketsCarousel(state.prematchOdds, prematchMarkets)}
            {state.showPrematchOdds && state.prematchOdds && renderBets(state.prematchOdds, 'Коефіцієнти (Prematch)')}
            {state.showStats && state.matchStats?.home_stats && state.matchStats?.away_stats && renderMatchStats(state.matchStats)}


        </div>
    );
}

export default React.memo(MatchPage);
